// components/ProfileView.tsx
import React, { useState, useEffect, useRef } from 'react';
import SubscriptionPlansView from './SubscriptionPlansView';
import { MessageTemplates, DEFAULT_TEMPLATES, loadMessageTemplates, saveMessageTemplates } from '../utils/messageTemplates';
import {
  Lock,
  User,
  LogOut,
  ExternalLink,

  CheckCircle,
  Settings,
  Shield,
  Building2,
  Globe,
  Phone,
  Mail,

  Send,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  Linkedin,
  Twitter,
  Smartphone,
  Tag,
  X,
} from 'lucide-react';
import { GatewayVerificationModal } from './GatewayVerificationInfo';
import { AuthUser, upgradeToOrganization } from '../utils/authApi';
import { getPermissions } from '../utils/permissions';
import { getCurrencySymbol, getCurrencyDisplay } from '../utils/currency';
import CurrencySelector from './CurrencySelector';
import { COUNTRY_CODES as PHONE_COUNTRY_CODES, POPULAR_COUNTRIES as PHONE_POPULAR } from '../utils/countryCodes';
import StatePicker from './StatePicker';
import { getOrderedCountryList } from '../utils/countryNames';
import { getDefaultSchemeForCountryAndRegion } from '../utils/taxSchemes';
import { inspectTaxId, TaxIdInfo } from '../utils/taxId';

// Master set of all countries that have sub-national tax regions, states, or
// exempt zones (matches Product Catalog / mobile app) — used as a fallback
// alongside scheme.region_based so this screen shows the State/Province
// picker for the same countries Product Catalog does.
const REGIONAL_TAX_COUNTRIES = new Set([
  'IN', 'US', 'CA', 'AU', 'BR', 'AR', 'PK', 'ES', 'PT', 'CH', 'NG',
  'MX', 'CO', 'ID', 'PE', 'MY', 'PH', 'CL', 'CN', 'RU', 'EC', 'VN',
  'DE', 'IT', 'FI', 'NO', 'DK', 'AT', 'BA', 'KR', 'TH', 'IL',
]);

// ─── Types ────────────────────────────────────────────────────────────────────
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
export type ContactEntryType =
  | 'email'
  | 'phone'
  | 'whatsapp'
  | 'telegram'
  | 'linkedin'
  | 'twitter'
  | 'other'
  | 'custom';

export interface ContactEntry {
  id: string;
  type: ContactEntryType;
  label: string; // only used when type === 'custom'
  value: string;
  showInFooter: boolean;
}

export interface CompanyProfile {
  name: string;
  domain: string;
  contacts: ContactEntry[];
  senderAccount: 'google' | 'company';
  notifyEmail: boolean;
  notifyWhatsApp: boolean;
  emailMessage: string;
  upiId: string;
  taxId: string;
}

export const EMPTY_COMPANY_PROFILE: CompanyProfile = {
  name: '',
  domain: '',
  contacts: [],
  senderAccount: 'google',
 notifyEmail: true,
  notifyWhatsApp: false,
  emailMessage: '',
  upiId: '',
  taxId: '',
};

export const CONTACT_TYPE_META: Record<
  ContactEntryType,
  { label: string; placeholder: string; icon: React.ReactNode }
> = {
  email: {
    label: 'Company email',
    placeholder: 'hello@yourcompany.com',
    icon: <Mail className="w-3.5 h-3.5" />,
  },
  phone: {
    label: 'Phone',
    placeholder: '+91 98765 43210',
    icon: <Phone className="w-3.5 h-3.5" />,
  },
  whatsapp: {
    label: 'WhatsApp',
    placeholder: '+91 98765 43210',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
  },
  telegram: {
    label: 'Telegram',
    placeholder: '@yourusername',
    icon: <Send className="w-3.5 h-3.5" />,
  },
  linkedin: {
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/company/...',
    icon: <Linkedin className="w-3.5 h-3.5" />,
  },
  twitter: {
    label: 'Twitter / X',
    placeholder: '@yourhandle',
    icon: <Twitter className="w-3.5 h-3.5" />,
  },
  other: {
    label: 'Other app',
    placeholder: 'App ID / number',
    icon: <Smartphone className="w-3.5 h-3.5" />,
  },
  custom: {
    label: 'Custom label',
    placeholder: 'Enter value...',
    icon: <Tag className="w-3.5 h-3.5" />,
  },
};

const STORAGE_KEY = 'cloudairambo_company_profile';

const PREFIX_COLORS = [
  { bg: '#fffbeb', border: '#fde68a', label: '#d97706', value: '#92400e' },
  { bg: '#f5f3ff', border: '#c4b5fd', label: '#7c3aed', value: '#4c1d95' },
  { bg: '#eff6ff', border: '#bfdbfe', label: '#1d4ed8', value: '#1e3a8a' },
  { bg: '#f0fdf4', border: '#86efac', label: '#16a34a', value: '#14532d' },
  { bg: '#fdf2f8', border: '#fbcfe8', label: '#db2777', value: '#831843' },
];

const PARTNER_SIGNUP_LINKS: Record<string, { url: string; note: string }> = {
  razorpay: {
    url: 'https://rzp.io/rzp/9XcG4toO',
    note: 'Fast-track onboarding for new merchant accounts',
  },
  payu: {
    url: 'https://onboarding.payu.in/app/account?partner_name=&partner_source=Affiliate+Links&partner_uuid=11f1-8b14-42b0e350-b8b9-0aad783eb813&source=Partner',
    note: 'Fast-track onboarding for new merchant accounts',
  },
};

// Compact phone-country dropdown used inside the unified outlet card —
// same search/select UX as the standalone phone-country picker, just
// without its own Save button (the parent card saves everything at once).
function InlinePhoneCountrySelect({ value, onChange, placeholder }: { value: string; onChange: (c: string) => void; placeholder?: string }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const all = Object.keys(PHONE_COUNTRY_CODES);
  const filtered = search
    ? all.filter(c => c.toLowerCase().includes(search.toLowerCase()) || PHONE_COUNTRY_CODES[c]?.includes(search.replace('+', '')))
    : [...PHONE_POPULAR, ...all.filter(c => !PHONE_POPULAR.includes(c))];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-xs font-bold transition-all ${
          value ? 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-white' : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
        }`}
      >
        <span>{value ? `🌍 ${value}  +${PHONE_COUNTRY_CODES[value] || '91'}` : (placeholder || '🌍 Not set — using org default')}</span>
        <span className="text-[10px] opacity-60">▾ {value ? 'change' : 'set for this outlet'}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-xl shadow-lg mt-1 overflow-hidden flex flex-col" style={{ maxHeight: 220 }}>
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              placeholder="Search country or +code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs outline-none"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(c => (
              <button
                key={c}
                onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs hover:bg-slate-50 transition-colors ${c === value ? 'bg-blue-50 font-bold text-blue-700' : 'text-slate-700'}`}
              >
                <span className="font-bold text-blue-500 w-8">+{PHONE_COUNTRY_CODES[c]}</span>
                <span>{c}</span>
                {c === value && <span className="ml-auto text-blue-500 text-[10px]">✓ selected</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One unified per-outlet card: Currency, Country, State/Province, and
// Default Phone Country — each field falls back to the org-wide default
// (shown as a badge) until the outlet sets its own value. Replaces the
// three previously separate Currency / Country & State / Phone Country
// sections with a single professional card, and saves all four fields
// together in one PATCH.
function OutletUnifiedLocationSection({ authUser, activeCurrency, outletId, orgTaxId }: { authUser: AuthUser; activeCurrency: string; outletId?: number | null; orgTaxId?: string }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [currency, setCurrency] = useState('');
  const [currencyMode, setCurrencyMode] = useState<'symbol' | 'code'>('symbol');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('');
  const [taxId, setTaxId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const orgPhoneDefault = (authUser as any)?.default_phone_country || localStorage.getItem('default_phone_country') || 'IN';

  const selectOutlet = (id: number, list?: any[]) => {
    const source = list || outlets;
    const o = source.find(x => x.id === id);
    if (!o) return;
    setSelectedId(id);
    setCurrency(o.currency || '');
    setCurrencyMode(o.currency_display_mode === 'code' ? 'code' : 'symbol');
    setCountry(o.country || '');
    setRegion(o.region || '');
    setPhoneCountry(o.default_phone_country || '');
    setTaxId(o.tax_id || '');
    setSaved(false);
  };

  useEffect(() => {
    if (!authUser?.org_id) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d) || d.length === 0) return;
        setOutlets(d);
        // Default to whatever branch is selected in the navbar, not
        // just outlets[0] — otherwise it's easy to save settings onto
        // the wrong outlet without noticing.
        const navMatch = outletId != null && d.some((o: any) => o.id === outletId);
        selectOutlet(navMatch ? outletId! : d[0].id, d);
      })
      .catch(() => {});
  }, [authUser]);

  // Keep this card's selected branch in sync whenever the navbar outlet changes
  useEffect(() => {
    if (outletId == null) return;
    if (outlets.some(o => o.id === outletId) && outletId !== selectedId) selectOutlet(outletId);
  }, [outletId, outlets]);

  const handleCountryChange = (c: string) => { setCountry(c); setRegion(''); setSaved(false); };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(`${BACKEND}/outlets/${selectedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          currency: currency || null,
          currency_display_mode: currencyMode,
          country: country || null,
          region: region || null,
          default_phone_country: phoneCountry || null,
          tax_id: taxId || null,
        }),
      });
      const updated = await res.json();
      if (!res.ok) {
        // Previously a 4xx/5xx body still got parsed as JSON and treated
        // as success — this surfaces the real failure instead.
        setSaveError(updated?.detail || `Save failed (${res.status})`);
        return;
      }
      setOutlets(prev => prev.map(o => o.id === selectedId ? { ...o, ...updated } : o));
      // Broadcast the FULL updated outlet — Quick Invoice, Product Catalog
      // and IAM all listen for this to update live, no page refresh.
      window.dispatchEvent(new CustomEvent('outlet-location-updated', { detail: { outletId: selectedId, outlet: updated, country, region } }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      console.warn('Outlet settings save failed:', e);
      setSaveError(e?.message || 'Network error — could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  // Picks up country/region changes saved from ANY screen (Product
  // Catalog, IAM outlets tab) while this card is mounted.
  useEffect(() => {
    const handler = (e: any) => {
      const o = e.detail?.outlet;
      setOutlets(prev => prev.map(x => {
        if (x.id !== e.detail?.outletId) return x;
        return o ? { ...x, ...o } : { ...x, country: e.detail.country, region: e.detail.region };
      }));
      if (e.detail?.outletId === selectedId) {
        setCountry((o?.country ?? e.detail.country) || '');
        setRegion((o?.region ?? e.detail.region) || '');
      }
    };
    window.addEventListener('outlet-location-updated', handler);
    return () => window.removeEventListener('outlet-location-updated', handler);
  }, [selectedId]);

  if (outlets.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-4">No outlets found yet.</div>;
  }

  const selectedOutlet = outlets.find(o => o.id === selectedId) || null;
  const scheme = country ? getDefaultSchemeForCountryAndRegion(country, region) : null;
  const usingFallbackCurrency = !currency;
  const usingFallbackPhone = !phoneCountry;
  const effectiveCurrency = currency || activeCurrency;
  const effectivePhoneCountry = phoneCountry || orgPhoneDefault;

  return (
    <div className="space-y-5">
      <OutletContextPill outlet={selectedOutlet} />
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => selectOutlet(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}

      {/* Currency */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Currency</label>
          {usingFallbackCurrency && (
            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Using org default · {activeCurrency}</span>
          )}
        </div>
        <CurrencySelector value={effectiveCurrency} onChange={c => { setCurrency(c); setSaved(false); }} />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setCurrencyMode('symbol'); setSaved(false); }}
            className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${currencyMode === 'symbol' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
          >
            {getCurrencySymbol(effectiveCurrency)} Symbol
          </button>
          <button
            onClick={() => { setCurrencyMode('code'); setSaved(false); }}
            className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${currencyMode === 'code' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
          >
            {effectiveCurrency.toUpperCase()} Code
          </button>
        </div>
      </div>

      {/* Country & State */}
      <div className="space-y-1.5 pt-4 border-t border-slate-100">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Country</label>
        <select
          value={country}
          onChange={e => handleCountryChange(e.target.value)}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="">-- Select country --</option>
          {getOrderedCountryList().map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>
      {country && (scheme?.region_based || REGIONAL_TAX_COUNTRIES.has(country)) && (
        <div className="space-y-1.5">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">State / Province</label>
          <StatePicker
            countryCode={country}
            value={region}
            onChange={r => { setRegion(r); setSaved(false); }}
            placeholder="State / Province"
          />
        </div>
      )}
      {country && scheme && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-[10px] text-slate-500">
          Tax scheme: <span className="font-bold text-slate-700">{scheme.label}</span> — drives the CGST/SGST/IGST-style breakdown on invoices and products.
        </div>
      )}
      {!country && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[10px] text-amber-700">
          No country set for this outlet yet — the tax breakdown won't show until one is chosen.
        </div>
      )}

      {/* Default phone country */}
      <div className="space-y-1.5 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Default Phone Country</label>
          {usingFallbackPhone && (
            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Falls back to org default · {orgPhoneDefault}</span>
          )}
        </div>
        <InlinePhoneCountrySelect
          value={phoneCountry}
          onChange={c => { setPhoneCountry(c); setSaved(false); }}
          placeholder={`🌍 Not set — inherits ${orgPhoneDefault} from org default`}
        />
        {usingFallbackPhone && (
          <button
            type="button"
            onClick={() => { setPhoneCountry(''); setSaved(false); }}
            className="text-[9px] font-bold text-slate-400 hover:text-slate-600 underline underline-offset-2"
          >
            (already cleared — pick a country above to override for this outlet only)
          </button>
        )}
        <p className="text-[9px] text-slate-400">Pre-fills the country dial code on phone fields for this branch. Leave unset to keep following the org default.</p>
      </div>

      {/* Tax ID — validated against THIS outlet's own country/region, since GSTIN-style IDs are per-state */}
      <div className="space-y-1.5 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Tax ID</label>
          {!taxId && orgTaxId && (
            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Using org default</span>
          )}
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder={orgTaxId ? `Not set — inherits ${orgTaxId} from org default` : 'e.g. GSTIN, VAT No.'}
            maxLength={25}
            value={taxId}
            onChange={e => { setTaxId(e.target.value.replace(/[\s\-\.]/g, '').toUpperCase()); setSaved(false); }}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:bg-white transition-all"
            style={{ paddingRight: 80, textTransform: 'uppercase', letterSpacing: 0.5 }}
          />
          {taxId.length > 0 && (() => {
            const meta = inspectTaxId(taxId, country);
            return (
              <div style={{ position: 'absolute', right: 10, pointerEvents: 'none' }}>
                {meta.isComplete || meta.currentLength >= meta.expectedLength ? (
                  <span className="text-[11px] font-black" style={{ color: meta.isValid ? '#16a34a' : '#ef4444' }}>
                    {meta.isValid ? '✓ Valid' : '✗ Invalid'}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-slate-400">{meta.currentLength}/{meta.expectedLength}</span>
                )}
              </div>
            );
          })()}
        </div>
        {taxId.length >= 2 && (() => {
          const meta = inspectTaxId(taxId, country);
          return (
            <div
              className="flex items-center rounded-xl px-2.5 py-1.5 gap-1.5 text-[11px]"
              style={{
                backgroundColor: (meta.isComplete && !meta.isValid) ? '#fef2f2' : meta.isValid ? '#f0fdf4' : '#eff6ff',
                border: `1px solid ${(meta.isComplete && !meta.isValid) ? '#fecaca' : meta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
              }}
            >
              <span style={{ fontSize: 13 }}>{meta.countryFlag}</span>
              <span className="font-bold text-slate-700 flex-1">
                {meta.countryName}
                {meta.detectedRegion ? ` · ${meta.detectedRegion}` : ''}
                <span className="text-slate-400 font-medium"> ({meta.taxLabel})</span>
              </span>
              {meta.detectedRegion && meta.detectedRegion !== region && (
                <span className="text-[9px] font-black text-amber-600">⚠ ≠ selected state</span>
              )}
              {meta.detectedRegion && meta.detectedRegion === region && (
                <span className="text-[9px] font-black text-emerald-600">✓ Matches</span>
              )}
            </div>
          );
        })()}
        <p className="text-[9px] text-slate-400">Leave unset to inherit the org-wide Tax ID (set in Company tab). Only override if this outlet is registered under a different tax number (e.g. a separate state GSTIN).</p>
      </div>

      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-[10px] text-red-700 font-semibold">
          ❌ {saveError}
        </div>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2.5 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save Outlet Settings'}
      </button>
    </div>
  );
}



function OutletContextPill({ outlet }: { outlet: { name: string } | null }) {
  if (outlet) {
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold px-2.5 py-1 rounded-full mb-3">
        🏪 {outlet.name}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-500 text-[11px] font-bold px-2.5 py-1 rounded-full mb-3">
      🌐 Org-wide default
    </span>
  );
}

function OutletPhoneCountryCard({ outlet, onSaved }: { outlet: any; onSaved: (id: number, code: string) => void }) {
  const [value, setValue] = useState(outlet.default_phone_country || 'IN');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const all = Object.keys(PHONE_COUNTRY_CODES);
  const filtered = search
    ? all.filter(c => c.toLowerCase().includes(search.toLowerCase()) || PHONE_COUNTRY_CODES[c]?.includes(search.replace('+', '')))
    : [...PHONE_POPULAR, ...all.filter(c => !PHONE_POPULAR.includes(c))];

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${outlet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ default_phone_country: value }),
      });
      onSaved(outlet.id, value);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Phone country save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-base">📱</span>
        <p className="text-xs font-extrabold text-slate-800">Default Phone Country — {outlet.name}</p>
      </div>
      <p className="text-[9px] text-slate-400">Pre-fills the country dial code on phone fields for this branch.</p>
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 hover:bg-white transition-all"
        >
          <span>🌍 {value} &nbsp;+{PHONE_COUNTRY_CODES[value] || '91'}</span>
          <span className="text-slate-400 text-[10px]">▾ change</span>
        </button>
        {open && (
          <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-xl shadow-lg mt-1 overflow-hidden flex flex-col" style={{ maxHeight: 240 }}>
            <div className="p-2 border-b border-slate-100">
              <input
                autoFocus
                placeholder="Search country or +code..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs outline-none"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.map(c => (
                <button
                  key={c}
                  onClick={() => { setValue(c); setOpen(false); setSearch(''); setSaved(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs hover:bg-slate-50 transition-colors ${c === value ? 'bg-blue-50 font-bold text-blue-700' : 'text-slate-700'}`}
                >
                  <span className="font-bold text-blue-500 w-8">+{PHONE_COUNTRY_CODES[c]}</span>
                  <span>{c}</span>
                  {c === value && <span className="ml-auto text-blue-500 text-[10px]">✓ selected</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function OutletCurrencyCard({ outlet, onSaved }: { outlet: any; onSaved: (id: number, currency: string, mode: string) => void }) {
  const [currency, setCurrency] = useState(outlet.currency || 'INR');
  const [mode, setMode] = useState<'symbol' | 'code'>(outlet.currency_display_mode === 'code' ? 'code' : 'symbol');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${outlet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currency, currency_display_mode: mode }),
      });
      onSaved(outlet.id, currency, mode);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Currency save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-base">💱</span>
        <p className="text-xs font-extrabold text-slate-800">Currency — {outlet.name}</p>
      </div>
      <p className="text-[9px] text-slate-400">Sets the invoice currency and display format for this branch.</p>
      <CurrencySelector value={currency} onChange={c => { setCurrency(c); setSaved(false); }} />
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => { setMode('symbol'); setSaved(false); }}
          className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${mode === 'symbol' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
        >
          {getCurrencySymbol(currency)} Symbol
        </button>
        <button
          onClick={() => { setMode('code'); setSaved(false); }}
          className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${mode === 'code' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
        >
          {currency.toUpperCase()} Code
        </button>
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}


function OutletsCountryRegionSection({ authUser }: { authUser: AuthUser }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    if (!authUser?.org_id) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d) || d.length === 0) return;
        setOutlets(d);
        setSelectedId(d[0].id);
        setCountry(d[0].country || '');
        setRegion(d[0].region || '');
      })
      .catch(() => {});
  }, [authUser]);

  const selectOutlet = (id: number) => {
    const o = outlets.find(x => x.id === id);
    if (!o) return;
    setSelectedId(id);
    setCountry(o.country || '');
    setRegion(o.region || '');
    setSaved(false);
  };

  const handleCountryChange = (c: string) => {
    setCountry(c);
    setRegion('');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${selectedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ country, region }),
      });
      setOutlets(prev => prev.map(o => o.id === selectedId ? { ...o, country, region } : o));
      window.dispatchEvent(new CustomEvent('outlet-location-updated', { detail: { outletId: selectedId, country, region } }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Country/region save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  // Picks up country/region changes saved from ANY screen (e.g. Product
  // Catalog's outlet country/state picker) while this component is
  // mounted, so both screens stay in sync without a manual refresh.
  useEffect(() => {
    const handler = (e: any) => {
      const o = e.detail?.outlet;
      setOutlets(prev => prev.map(x => {
        if (x.id !== e.detail?.outletId) return x;
        return o ? { ...x, ...o } : { ...x, country: e.detail.country, region: e.detail.region };
      }));
      if (e.detail?.outletId === selectedId) {
        setCountry((o?.country ?? e.detail.country) || '');
        setRegion((o?.region ?? e.detail.region) || '');
      }
    };
    window.addEventListener('outlet-location-updated', handler);
    return () => window.removeEventListener('outlet-location-updated', handler);
  }, [selectedId]);

  if (outlets.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-4">No outlets found yet.</div>;
  }

  const selectedOutletObj = outlets.find(o => o.id === selectedId) || null;
  const scheme = getDefaultSchemeForCountryAndRegion(country, region);

  return (
    <div className="space-y-3">
      <OutletContextPill outlet={selectedOutletObj} />
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => selectOutlet(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-emerald-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Country</label>
        <select
          value={country}
          onChange={e => handleCountryChange(e.target.value)}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        >
          <option value="">-- Select country --</option>
          {getOrderedCountryList().map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>
      {country && scheme.region_based && (
        <div className="space-y-1.5">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">State / Province</label>
          <StatePicker
            countryCode={country}
            value={region}
            onChange={r => { setRegion(r); setSaved(false); }}
            placeholder="State / Province"
          />
        </div>
      )}
      {country && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-[10px] text-slate-500">
          Tax scheme: <span className="font-bold text-slate-700">{scheme.label}</span> — drives the CGST/SGST/IGST-style breakdown shown on invoices and the product catalog.
        </div>
      )}
      <button
        onClick={handleSave}
        disabled={saving || !country}
        className={`w-full py-2 rounded-lg font-bold text-xs transition-all disabled:opacity-50 ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function OutletsCurrencySection({ authUser }: { authUser: AuthUser }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [currency, setCurrency] = useState('INR');
  const [mode, setMode] = useState<'symbol' | 'code'>('symbol');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    if (!authUser?.org_id) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d) || d.length === 0) return;
        setOutlets(d);
        setSelectedId(d[0].id);
        setCurrency(d[0].currency || 'INR');
        setMode(d[0].currency_display_mode === 'code' ? 'code' : 'symbol');
      })
      .catch(() => {});
  }, [authUser]);

  const selectOutlet = (id: number) => {
    const o = outlets.find(x => x.id === id);
    if (!o) return;
    setSelectedId(id);
    setCurrency(o.currency || 'INR');
    setMode(o.currency_display_mode === 'code' ? 'code' : 'symbol');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${selectedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currency, currency_display_mode: mode }),
      });
      setOutlets(prev => prev.map(o => o.id === selectedId ? { ...o, currency, currency_display_mode: mode } : o));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Currency save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  if (outlets.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-4">No outlets found yet.</div>;
  }

  const selectedOutletObj = outlets.find(o => o.id === selectedId) || null;

  return (
    <div className="space-y-3">
      <OutletContextPill outlet={selectedOutletObj} />
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => selectOutlet(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-emerald-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}
      <CurrencySelector value={currency} onChange={c => { setCurrency(c); setSaved(false); }} />
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => { setMode('symbol'); setSaved(false); }}
          className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${mode === 'symbol' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
        >
          {getCurrencySymbol(currency)} Symbol
        </button>
        <button
          onClick={() => { setMode('code'); setSaved(false); }}
          className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${mode === 'code' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
        >
          {currency.toUpperCase()} Code
        </button>
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function OutletsInvoicePrefixSection({ authUser }: { authUser: AuthUser }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    if (!authUser?.org_id) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setOutlets(d); })
      .catch(() => {});
  }, [authUser]);

  const handleSaved = (id: number, prefix: string) => {
    setOutlets(prev => prev.map(o => o.id === id ? { ...o, invoice_prefix: prefix } : o));
  };

  if (outlets.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-4">No outlets found yet.</div>;
  }

  return (
    <div className="space-y-3">
      {outlets.map(o => (
        <OutletInvoicePrefixCard key={o.id} outlet={o} onSaved={handleSaved} />
      ))}
    </div>
  );
}

function OutletsPhoneCountrySection({ authUser }: { authUser: AuthUser }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [value, setValue] = useState('IN');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    if (!authUser?.org_id) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d) || d.length === 0) return;
        setOutlets(d);
        setSelectedId(d[0].id);
        setValue(d[0].default_phone_country || 'IN');
      })
      .catch(() => {});
  }, [authUser]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectOutlet = (id: number) => {
    const o = outlets.find(x => x.id === id);
    if (!o) return;
    setSelectedId(id);
    setValue(o.default_phone_country || 'IN');
    setSaved(false);
  };

  const all = Object.keys(PHONE_COUNTRY_CODES);
  const filtered = search
    ? all.filter(c => c.toLowerCase().includes(search.toLowerCase()) || PHONE_COUNTRY_CODES[c]?.includes(search.replace('+', '')))
    : [...PHONE_POPULAR, ...all.filter(c => !PHONE_POPULAR.includes(c))];

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${selectedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ default_phone_country: value }),
      });
      setOutlets(prev => prev.map(o => o.id === selectedId ? { ...o, default_phone_country: value } : o));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Phone country save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  if (outlets.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-4">No outlets found yet.</div>;
  }

  const selectedPhoneOutletObj = outlets.find(o => o.id === selectedId) || null;

  return (
    <div className="space-y-3">
      <OutletContextPill outlet={selectedPhoneOutletObj} />
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => selectOutlet(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 hover:bg-white transition-all"
        >
          <span>🌍 {value} &nbsp;+{PHONE_COUNTRY_CODES[value] || '91'}</span>
          <span className="text-slate-400 text-[10px]">▾ change</span>
        </button>
        {open && (
          <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-xl shadow-lg mt-1 overflow-hidden flex flex-col" style={{ maxHeight: 240 }}>
            <div className="p-2 border-b border-slate-100">
              <input
                autoFocus
                placeholder="Search country or +code..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs outline-none"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.map(c => (
                <button
                  key={c}
                  onClick={() => { setValue(c); setOpen(false); setSearch(''); setSaved(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs hover:bg-slate-50 transition-colors ${c === value ? 'bg-blue-50 font-bold text-blue-700' : 'text-slate-700'}`}
                >
                  <span className="font-bold text-blue-500 w-8">+{PHONE_COUNTRY_CODES[c]}</span>
                  <span>{c}</span>
                  {c === value && <span className="ml-auto text-blue-500 text-[10px]">✓ selected</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}
function OutletInvoicePrefixCard({ outlet, onSaved }: { outlet: any; onSaved: (id: number, prefix: string) => void }) {
  const [prefix, setPrefix] = useState(outlet.invoice_prefix || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const handleSave = async () => {
    const clean = prefix.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!clean) { setError('Enter a prefix first'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND}/outlets/${outlet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoice_prefix: clean }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Save failed — try again');
        return;
      }
      setPrefix(clean);
      onSaved(outlet.id, clean);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Invoice prefix save failed:', e);
      setError('Save failed — try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-base">🧾</span>
        <p className="text-xs font-extrabold text-slate-800">Invoice Prefix — {outlet.name}</p>
      </div>
      <p className="text-[9px] text-slate-400">POS receipts and invoices from this outlet will use this prefix (e.g. MUM-REC-00001). Not currently checked for uniqueness across outlets — pick something distinct.</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={prefix}
          onChange={e => { setPrefix(e.target.value.toUpperCase()); setSaved(false); setError(''); }}
          placeholder="e.g. MUM"
          maxLength={6}
          className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase"
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      {error && <p className="text-[9px] text-red-500 font-semibold">{error}</p>}
    </div>
  );
}
function OutletUpiCard({ outlet, onSaved }: { outlet: any; onSaved: (id: number, upiId: string) => void }) {
  const [upiId, setUpiId] = useState(outlet.upi_id || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${outlet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ upi_id: upiId.trim() }),
      });
      onSaved(outlet.id, upiId.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('UPI save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-base">💳</span>
        <p className="text-xs font-extrabold text-slate-800">UPI ID — {outlet.name}</p>
      </div>
      <p className="text-[9px] text-slate-400">No gateway needed — just enter a UPI ID and the POS display QR will open GPay / PhonePe / Paytm directly for this outlet.</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={upiId}
          onChange={e => { setUpiId(e.target.value); setSaved(false); }}
          placeholder="outletname@okaxis"
          className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
// ─── Storage helpers ──────────────────────────────────────────────────────────
function PaymentGatewayCard({ authUser, outletId, outletLabel }: { authUser: AuthUser; outletId: number | null; outletLabel?: string }) {

  const token = () => localStorage.getItem('cloudairambo_jwt') || '';
  const outletQS = outletId != null ? `?outlet_id=${outletId}` : '';
  const outletQSAmp = outletId != null ? `&outlet_id=${outletId}` : '';

  const [allGateways, setAllGateways] = useState<any[]>([]);
  const [connected, setConnected] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<any>(null);
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [extraConfig, setExtraConfig] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('All');
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    // Load all gateways from backend
    fetch(`${BACKEND}/gateway/list`)
      .then(r => r.json())
      .then(d => {
        setAllGateways(d.gateways || []);
        // Only default-select the first gateway the very first time the list
        // loads — never overwrite a selection the user already made (this
        // was resetting the form back to Stripe on every branch switch/refetch).
        setSelected(prev => prev ?? (d.gateways?.[0] ?? null));
      })
      .catch(() => {});

    // Load connected status
    fetch(`${BACKEND}/gateway/status${outletQS}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => {
        const map: Record<string, boolean> = {};
        (d.gateways || []).forEach((g: any) => { map[g.gateway] = true; });
        setConnected(map);
      })
      .catch(() => {});
  }, [authUser, outletId]);

  const regions = ['All', ...Array.from(new Set(allGateways.map((g: any) => g.region.split('/')[0])))];

  const filtered = allGateways.filter((g: any) => {
    const matchSearch = g.name.toLowerCase().includes(search.toLowerCase()) ||
                        g.region.toLowerCase().includes(search.toLowerCase());
    const matchRegion = regionFilter === 'All' || g.region.includes(regionFilter);
    return matchSearch && matchRegion;
  });

  const handleSelect = (gw: any) => {
    setSelected(gw);
    setApiKey(''); setSecretKey(''); setWebhookSecret(''); setExtraConfig({});
    setError(''); setTestResult(null); setSaved(false);
  };

  const requiredExtraMissing = () => {
    const required = selected?.extra_fields || {};
    return Object.keys(required).some(k => !extraConfig[k]?.trim());
  };

  const handleTest = async () => {
    if (!apiKey.trim() || !secretKey.trim()) { setError('Both fields are required'); return; }
    if (requiredExtraMissing()) { setError('Please fill in all required fields'); return; }
    setTesting(true); setTestResult(null); setError('');
    try {
      const res = await fetch(`${BACKEND}/gateway/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ gateway: selected.id, api_key: apiKey.trim(), secret_key: secretKey.trim(), extra_config: extraConfig }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (e: any) {
      setTestResult({ valid: false, message: e.message });
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!apiKey.trim() || !secretKey.trim()) { setError('Both fields are required'); return; }
    if (requiredExtraMissing()) { setError('Please fill in all required fields'); return; }
    setSaving(true); setError(''); setTestResult(null);
    try {
      const mergedExtraConfig = webhookSecret.trim()
        ? { ...extraConfig, webhook_secret: webhookSecret.trim() }
        : extraConfig;
      const res = await fetch(`${BACKEND}/gateway/connect${outletQS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ gateway: selected.id, api_key: apiKey.trim(), secret_key: secretKey.trim(), extra_config: mergedExtraConfig }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Connection failed'); return; }
      setConnected(prev => ({ ...prev, [selected.id]: true }));
      setApiKey(''); setSecretKey(''); setWebhookSecret(''); setExtraConfig({});
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (gwId: string) => {
    if (!confirm(`Disconnect ${gwId}?`)) return;
    await fetch(`${BACKEND}/gateway/disconnect/${gwId}${outletQS}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token()}` }
    });
    setConnected(prev => ({ ...prev, [gwId]: false }));
  };

  const connectedList = allGateways.filter((g: any) => connected[g.id]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 border border-violet-100 text-base">💳</div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-extrabold text-slate-800">{outletLabel ? `Payment Gateways — ${outletLabel}` : 'Payment Gateways (Org-wide default)'}</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Connect any gateway so customers can pay bills online — 46 supported worldwide</p>
        </div>
        <button
          onClick={() => setShowInfoModal(true)}
          className="shrink-0 flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 border border-violet-200 px-2.5 py-1.5 rounded-lg transition-all"
        >
          ℹ️ How verification works
        </button>
      </div>

      {showInfoModal && <GatewayVerificationModal onClose={() => setShowInfoModal(false)} />}

      {/* Standalone merchant onboarding prompt */}
      {Object.keys(PARTNER_SIGNUP_LINKS).some(id => !connected[id]) && (
        <div className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-4">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-indigo-100 rounded-full opacity-40" />
          <div className="relative">
            <p className="text-xs font-extrabold text-slate-800">Don't have a merchant account yet?</p>
            <p className="text-[10.5px] text-slate-500 mt-0.5 leading-relaxed">
              Set up an account directly with a supported payment provider below.
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(PARTNER_SIGNUP_LINKS)
                .filter(([id]) => !connected[id])
                .map(([id, link]) => {
                  const gw = allGateways.find((g: any) => g.id === id);
                  return (
                    <a key={id} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2.5 bg-white border border-indigo-200 rounded-xl p-3 hover:border-indigo-400 hover:shadow-sm transition-all">
                      <span className="text-lg shrink-0">{gw?.logo || '💳'}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold text-slate-800">Create {gw?.name || id} Account</p>
                        <p className="text-[9px] text-slate-400 truncate">{link.note}</p>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    </a>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Connected gateways */}
      {connectedList.length > 0 && (
        <div className="space-y-2">
          <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Connected ({connectedList.length})</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {connectedList.map((g: any) => (
              <div key={g.id} className={`flex items-center justify-between p-2.5 rounded-xl border ${g.manual_confirmation ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-base">{g.logo}</span>
                  <div>
                    <p className="text-xs font-bold text-slate-800">{g.name}</p>
                    <p className={`text-[9px] font-bold ${g.manual_confirmation ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {g.manual_confirmation ? '● Connected · Manual confirmation only' : '● Connected'}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleDisconnect(g.id)}
                  className="text-[9px] font-bold text-red-500 hover:text-red-700 bg-white border border-red-200 px-2 py-1 rounded-lg">
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add new gateway */}
      <div className="space-y-3">
        <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Add Gateway</p>

        {/* Search + region filter */}
        <div className="flex gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search gateway..."
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-500" />
          <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)}
            className="px-2 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-500">
            {regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Gateway grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto pr-1">
          {filtered.map((g: any) => (
            <button key={g.id} onClick={() => handleSelect(g)}
              className={`flex items-center gap-1.5 p-2 rounded-xl border transition-all text-left ${
                selected?.id === g.id ? 'border-violet-500 bg-violet-50' :
                connected[g.id] ? 'border-emerald-300 bg-emerald-50' :
                'border-slate-200 hover:border-slate-300 bg-white'
              }`}>
              <span className="text-sm shrink-0">{g.logo}</span>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-800 truncate">{g.name}</p>
                <p className="text-[8px] text-slate-400 truncate">{g.region}</p>
              </div>
              {connected[g.id] && <span className="ml-auto text-emerald-500 text-[8px] shrink-0">✓</span>}
            </button>
          ))}
        </div>

        {/* Selected gateway form */}
        {selected && (
          <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50">
            <div className="flex items-center gap-2">
              <span className="text-lg">{selected.logo}</span>
              <div>
                <p className="text-sm font-extrabold text-slate-800">{selected.name}</p>
                <p className="text-[10px] text-slate-400">{selected.region}</p>
              </div>
              {!selected.verify && !selected.manual_confirmation && (
                <span className="ml-auto text-[9px] bg-amber-50 border border-amber-200 text-amber-600 font-bold px-2 py-0.5 rounded-full">
                  Saved without live verification
                </span>
              )}
            </div>

            {PARTNER_SIGNUP_LINKS[selected.id] && (
              <div className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-4">
                <div className="absolute -right-6 -top-6 w-24 h-24 bg-indigo-100 rounded-full opacity-40" />
                <div className="relative flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-xl bg-white shadow-sm border border-indigo-100 flex items-center justify-center text-lg">
                    {selected.logo}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-extrabold text-slate-800">New to {selected.name}?</p>
                    <p className="text-[10.5px] text-slate-500 mt-0.5 leading-relaxed">Direct business onboarding link to create a new {selected.name} merchant account.</p>
                    <a href={PARTNER_SIGNUP_LINKS[selected.id].url} target="_blank" rel="noopener noreferrer"
                      className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-3.5 py-1.5 rounded-lg transition-all shadow-sm">
                      Create {selected.name} Account
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <p className="text-[9px] text-slate-400 mt-1.5">{PARTNER_SIGNUP_LINKS[selected.id].note}</p>
                  </div>
                </div>
              </div>
            )}

            {selected.manual_confirmation && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-1">
                <p className="text-[11px] font-black text-red-700">⚠ Manual confirmation required</p>
                <p className="text-[10px] text-red-600 font-medium leading-relaxed">
                  {selected.name} doesn't support automatic payment confirmation yet. Customers can still pay using this gateway,
                  but the invoice will stay marked "Pending" until you manually check the gateway's dashboard and mark it paid yourself.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                  {selected.fields.api_key}
                </label>
                <input type="text" value={apiKey} onChange={e => { setApiKey(e.target.value); setError(''); setTestResult(null); }}
                  placeholder={selected.fields.api_key}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-violet-500" />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                  {selected.fields.secret_key}
                </label>
                <input type="password" value={secretKey} onChange={e => { setSecretKey(e.target.value); setError(''); setTestResult(null); }}
                  placeholder={selected.fields.secret_key}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-violet-500" />
              </div>
              {/* Upfront Webhook Endpoint URL with 1-click Copy */}
              <div className="bg-slate-100 border border-slate-200 rounded-xl p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-black uppercase tracking-wider text-slate-600">
                    Webhook Endpoint URL
                  </span>
                  <span className="text-[9px] text-slate-400">Paste in {selected.name} dashboard</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-3 py-1.5">
                  <span className="font-mono text-[10px] text-green-400 flex-1 truncate select-all">
                    {`${BACKEND}/pay/webhook/${selected.id}?org_id=${(authUser as any)?.org_id}${outletQSAmp}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${BACKEND}/pay/webhook/${selected.id}?org_id=${(authUser as any)?.org_id}${outletQSAmp}`);
                      alert('Webhook URL copied to clipboard!');
                    }}
                    className="shrink-0 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-[10px] font-bold transition-all"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Webhook Secret with Auto-Generate */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                    Webhook Secret <span className="text-slate-300 font-normal normal-case ml-1">optional</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const randomSecret = 'whsec_' + Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 12);
                      setWebhookSecret(randomSecret);
                    }}
                    className="text-[10px] font-bold text-violet-600 hover:text-violet-800"
                  >
                    🎲 Auto-Generate
                  </button>
                </div>
                <input
                  type="text"
                  value={webhookSecret}
                  onChange={e => { setWebhookSecret(e.target.value); setError(''); setTestResult(null); }}
                  placeholder="Paste or auto-generate secret"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
                <p className="text-[9px] text-slate-400">
                  If your payment provider lets you define a custom webhook secret, tap Auto-Generate and paste it in your provider settings. If your provider assigns a signing secret, paste it here.
                </p>
              </div>
              {Object.entries(selected.extra_fields || {}).map(([key, label]) => (
                <div key={key} className="space-y-1">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                    {label as string}
                  </label>
                  <input type="text" value={extraConfig[key] || ''}
                    onChange={e => { setExtraConfig(prev => ({ ...prev, [key]: e.target.value })); setError(''); setTestResult(null); }}
                    placeholder={label as string}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
              ))}
            </div>

            {testResult && (
              <div className={`px-3 py-2 rounded-lg text-[10px] font-semibold border ${
                testResult.valid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {testResult.valid ? '✅' : '❌'} {testResult.message}
              </div>
            )}

            {error && (
              <div className="px-3 py-2 rounded-lg text-[10px] font-semibold border bg-red-50 border-red-200 text-red-700">
                ❌ {error}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={handleTest} disabled={testing || !apiKey || !secretKey}
                className="flex-1 py-2 rounded-xl font-bold text-xs border border-violet-300 text-violet-700 hover:bg-violet-50 transition-all disabled:opacity-50">
                {testing ? 'Testing...' : '🔍 Test Connection'}
              </button>
              <button onClick={handleConnect} disabled={saving || !apiKey || !secretKey}
                className={`flex-1 py-2 rounded-xl font-bold text-xs transition-all disabled:opacity-50 ${
                  saved ? 'bg-emerald-600 text-white' : 'bg-violet-600 hover:bg-violet-700 text-white'
                }`}>
                {saved ? '✓ Connected!' : saving ? 'Saving...' : `Connect ${selected.name}`}
              </button>
            </div>
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-[10px] text-slate-500">
          🔒 Keys stored securely. Used only to create payment orders when customers click "Pay Now". Money goes directly to your account.
        </div>

        {/* Webhook URLs */}
        {connectedList.length > 0 && (
          <div className="space-y-2">
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              🔔 Webhook URLs — paste these in your gateway dashboard
            </p>
            <p className="text-[9px] text-slate-400">
              When a customer pays, the gateway calls this URL to automatically mark it paid in CloudAIRambo.
            </p>
            {connectedList.map((g: any) => {
              const orgId = (authUser as any)?.org_id;
              const webhookUrl = `${BACKEND}/pay/webhook/${g.id}?org_id=${orgId}${outletQSAmp}`;
              return (
                <div key={g.id} className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">{g.logo}</span>
                    <p className="text-[10px] font-bold text-slate-700">{g.name}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-900 rounded-xl px-3 py-2">
                    <p className="flex-1 font-mono text-[9px] text-green-400 truncate select-all">
                      {webhookUrl}
                    </p>
                    <button
                      onClick={() => navigator.clipboard.writeText(webhookUrl)}
                      className="shrink-0 px-2 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-[9px] font-bold transition-all"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              );
            })}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-2.5 text-[9px] text-blue-700 font-medium">
              💡 In your gateway dashboard, find "Webhooks" → "Add URL" → paste the URL above. Do this once per gateway — payments will then auto-confirm instantly.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export function loadCompanyProfile(): CompanyProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...EMPTY_COMPANY_PROFILE, ...JSON.parse(raw) };
  } catch (_) {}
  return { ...EMPTY_COMPANY_PROFILE };
}

export function saveCompanyProfile(profile: CompanyProfile): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}


function OutletWhatsAppBusinessAPICard({ outlet }: { outlet: any }) {
  const [phoneNumberId, setPhoneNumberId] = useState(outlet.wa_phone_number_id || '');
  const [accessToken, setAccessToken] = useState('');
  const [wabaId, setWabaId] = useState(outlet.wa_waba_id || '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await fetch(`${BACKEND}/whatsapp/business-api/test`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone_number_id: phoneNumberId, access_token: accessToken, waba_id: wabaId }),
      });
      setTestResult(await res.json());
    } catch (e: any) {
      setTestResult({ valid: false, message: e.message });
    } finally { setTesting(false); }
  };

  const handleConnect = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BACKEND}/outlets/${outlet.id}/whatsapp-business-api`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone_number_id: phoneNumberId, access_token: accessToken, waba_id: wabaId }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Connection failed'); return; }
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
      <p className="text-xs font-extrabold text-slate-800">WhatsApp Business API — {outlet.name}</p>
      <p className="text-[9px] text-slate-400">Get these from Meta Business Suite → WhatsApp → API Setup. Requires a Meta-verified business number.</p>
      {[
        { label: 'Phone Number ID', value: phoneNumberId, set: setPhoneNumberId },
        { label: 'WABA ID', value: wabaId, set: setWabaId },
      ].map(f => (
        <div key={f.label} className="space-y-1">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">{f.label}</label>
          <input type="text" value={f.value} onChange={e => f.set(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono" />
        </div>
      ))}
      <div className="space-y-1">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Permanent Access Token</label>
        <input type="password" value={accessToken} onChange={e => setAccessToken(e.target.value)}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono" />
      </div>
      {testResult && (
        <div className={`px-3 py-2 rounded-lg text-[10px] font-semibold border ${testResult.valid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {testResult.valid ? '✅' : '❌'} {testResult.message}
        </div>
      )}
      {error && (
        <div className="px-3 py-2 rounded-lg text-[10px] font-semibold border bg-red-50 border-red-200 text-red-700">
          ❌ {error}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={handleTest} disabled={testing || !phoneNumberId || !accessToken}
          className="flex-1 py-2 rounded-lg border border-green-300 text-green-700 font-bold text-xs">
          {testing ? 'Testing...' : '🔍 Test'}
        </button>
        <button onClick={handleConnect} disabled={saving || !phoneNumberId || !accessToken}
          className={`flex-1 py-2 rounded-lg font-bold text-xs text-white ${saved ? 'bg-emerald-600' : 'bg-green-600 hover:bg-green-700'}`}>
          {saved ? '✓ Connected' : saving ? 'Saving...' : 'Connect'}
        </button>
      </div>
    </div>
  );
}

function OutletsPaymentGatewaySection({ authUser, outletId }: { authUser: AuthUser; outletId?: number | null }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showOrgWide, setShowOrgWide] = useState(false);
  const [upiId, setUpiId] = useState('');
  const [upiSaving, setUpiSaving] = useState(false);
  const [upiSaved, setUpiSaved] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    if (!authUser?.org_id) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d)) return;
        setOutlets(d);
        const navMatch = outletId != null && d.some((o: any) => o.id === outletId);
        if (navMatch) {
          const match = d.find((o: any) => o.id === outletId);
          setSelectedId(outletId);
          setUpiId(match?.upi_id || '');
        } else if (d.length > 0) {
          setSelectedId(d[0].id); setUpiId(d[0].upi_id || '');
        }
      })
      .catch(() => {});
  }, [authUser]);

  // Keep this section's selected branch in sync whenever the navbar outlet changes
  useEffect(() => {
    if (outletId == null) return;
    const match = outlets.find(o => o.id === outletId);
    if (match) {
      setSelectedId(outletId);
      setUpiId(match.upi_id || '');
      setUpiSaved(false);
    }
  }, [outletId, outlets]);

  const selectOutlet = (id: number) => {
    const o = outlets.find(x => x.id === id);
    if (!o) return;
    setSelectedId(id);
    setUpiId(o.upi_id || '');
    setUpiSaved(false);
  };

  const handleUpiSave = async () => {
    if (!selectedId) return;
    setUpiSaving(true);
    try {
      await fetch(`${BACKEND}/outlets/${selectedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ upi_id: upiId.trim() }),
      });
      setOutlets(prev => prev.map(o => o.id === selectedId ? { ...o, upi_id: upiId.trim() } : o));
      setUpiSaved(true);
      setTimeout(() => setUpiSaved(false), 2000);
    } catch (e) {
      console.warn('UPI save failed:', e);
    } finally {
      setUpiSaving(false);
    }
  };

  if (outlets.length === 0) {
    return <PaymentGatewayCard authUser={authUser} outletId={null} />;
  }

  const selectedOutlet = outlets.find(o => o.id === selectedId);

  return (
    <div className="space-y-4">
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => selectOutlet(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-violet-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-base">💳</span>
          <p className="text-xs font-extrabold text-slate-800">UPI ID{selectedOutlet ? ` — ${selectedOutlet.name}` : ''}</p>
        </div>
        <p className="text-[9px] text-slate-400">No gateway needed — just enter a UPI ID and the POS display QR will open GPay / PhonePe / Paytm directly for this outlet.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={upiId}
            onChange={e => { setUpiId(e.target.value); setUpiSaved(false); }}
            placeholder="outletname@okaxis"
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleUpiSave}
            disabled={upiSaving}
            className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${upiSaved ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
          >
            {upiSaved ? '✓ Saved' : upiSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {selectedId && <PaymentGatewayCard authUser={authUser} outletId={selectedId} outletLabel={selectedOutlet?.name} />}

      <button
        onClick={() => setShowOrgWide(s => !s)}
        className="w-full py-2 text-[11px] font-bold text-slate-500 border border-dashed border-slate-300 rounded-xl hover:border-slate-400 hover:text-slate-700 transition-all"
      >
        {showOrgWide ? '▲ Hide org-wide default gateway' : '▼ Set an org-wide default (fallback) gateway'}
      </button>
      {showOrgWide && <PaymentGatewayCard authUser={authUser} outletId={null} />}
    </div>
  );
}

function OrgDefaultsCard({
  authUser,
  activeCurrency,
  currencyDisplayMode,
  onCurrencyChange,
  onCurrencyDisplayModeChange,
}: {
  authUser: AuthUser | null;
  activeCurrency: string;
  currencyDisplayMode: 'symbol' | 'code';
  onCurrencyChange: (currency: string) => void;
  onCurrencyDisplayModeChange: (mode: 'symbol' | 'code') => void;
}) {
  const [currency, setCurrency] = useState(activeCurrency);
  const [mode, setMode] = useState<'symbol' | 'code'>(currencyDisplayMode);
  const [phoneCountry, setPhoneCountry] = useState(localStorage.getItem('default_phone_country') || 'IN');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setCurrency(activeCurrency); }, [activeCurrency]);
  useEffect(() => { setMode(currencyDisplayMode); }, [currencyDisplayMode]);

  useEffect(() => {
    const token = localStorage.getItem('cloudairambo_jwt');
    if (!token || !authUser) return;
    fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.default_phone_country) {
          setPhoneCountry(data.default_phone_country);
          localStorage.setItem('default_phone_country', data.default_phone_country);
        }
        setCountry(data.default_country || '');
        setRegion(data.default_region || '');
      })
      .catch(() => {});
  }, [authUser]);

  const handleCountryChange = (c: string) => { setCountry(c); setRegion(''); setSaved(false); };
  const scheme = country ? getDefaultSchemeForCountryAndRegion(country, region) : null;

  const handleSave = async () => {
    setSaving(true);
    onCurrencyChange(currency);
    localStorage.setItem('cloudairambo_active_currency', currency);
    onCurrencyDisplayModeChange(mode);
    localStorage.setItem('default_phone_country', phoneCountry);
    sessionStorage.removeItem('detected_country');

    const token = localStorage.getItem('cloudairambo_jwt');
    if (token) {
      try {
        await fetch(`${BACKEND}/auth/me/defaults`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            default_phone_country: phoneCountry,
            default_country: country || null,
            default_region: region || null,
          }),
        });
      } catch (e) { console.warn('Org defaults save failed:', e); }
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      {/* Currency */}
      <div className="space-y-1.5">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Currency</label>
        <CurrencySelector value={currency} onChange={c => { setCurrency(c); setSaved(false); }} />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setMode('symbol'); setSaved(false); }}
            className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${mode === 'symbol' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
          >
            {getCurrencySymbol(currency)} Symbol
          </button>
          <button
            onClick={() => { setMode('code'); setSaved(false); }}
            className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${mode === 'code' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
          >
            {currency.toUpperCase()} Code
          </button>
        </div>
      </div>

      {/* Country */}
      <div className="space-y-1.5 pt-4 border-t border-slate-100">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Country</label>
        <select
          value={country}
          onChange={e => handleCountryChange(e.target.value)}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="">-- Select country --</option>
          {getOrderedCountryList().map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>
      {country && (scheme?.region_based || REGIONAL_TAX_COUNTRIES.has(country)) && (
        <div className="space-y-1.5">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">State / Province</label>
          <StatePicker
            countryCode={country}
            value={region}
            onChange={r => { setRegion(r); setSaved(false); }}
            placeholder="State / Province"
          />
        </div>
      )}
      {country && scheme && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-[10px] text-slate-500">
          Tax scheme: <span className="font-bold text-slate-700">{scheme.label}</span> — fallback for any outlet with no country/state of its own.
        </div>
      )}

      {/* Phone country */}
      <div className="space-y-1.5 pt-4 border-t border-slate-100">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Default Phone Country</label>
        <InlinePhoneCountrySelect value={phoneCountry} onChange={c => { setPhoneCountry(c); setSaved(false); }} />
        <p className="text-[9px] text-slate-400">Pre-fills the country dial code across Credit Book, POS, Inventory and Invoice.</p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className={`w-full py-2.5 rounded-lg font-bold text-xs transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
      >
        {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save Organization Defaults'}
      </button>
    </div>
  );
}
// ─── Props ────────────────────────────────────────────────────────────────────

interface ProfileViewProps {
  authUser: AuthUser | null;
  googleUser: any | null;
  googleToken: string | null;
  activeCurrency: string;
  outletId: number | null;
  storageOption: 'local' | 'drive' | 'cloud' | null;
  onCurrencyChange: (currency: string) => void;
  onStorageChange: (mode: 'local' | 'drive' | 'cloud') => void;
  onLogin: () => void;
  onLogout: () => void;
  onGoogleLogin: () => void;
  onGoogleLogout: () => void;
  onShowAuth: () => void;
  onUpgrade: (updatedUser: AuthUser) => void;
  onShowPlans?: () => void;
  moduleStatuses?: Record<string, string>;
  moduleTrialEndDates?: Record<string, string>;
  modulePeriodEndDates?: Record<string, string>;
}

function CashierBadgeCard({ authUser }: { authUser: AuthUser }) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const userId = (authUser as any)?.id || (authUser as any)?.user_id;

    const generateQR = async (tokenValue: string) => {
      try {
        const qrModule: any = await import('qrcode');
        const QRCode = qrModule.default || qrModule;
        const qrValue = `CASHIER_TOKEN:${tokenValue}`;
        const url = await QRCode.toDataURL(qrValue, {
          width: 260,
          margin: 1,
          color: {
            dark: '#0f172a',
            light: '#ffffff'
          }
        });
        setQrDataUrl(url);
        setError('');
      } catch (err: any) {
        console.error('QR rendering error:', err);
        setError('Failed to render QR image');
      } finally {
        setLoading(false);
      }
    };

    if (!userId) {
      if (jwt) generateQR(jwt);
      else { setError('Session expired'); setLoading(false); }
      return;
    }

    fetch(`${BACKEND}/iam/users/${userId}/badge-token`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || `Server error (${r.status})`);
        const badgeToken = data.token || data.access_token;
        if (!badgeToken) throw new Error('No badge token returned');
        return badgeToken;
      })
      .then((token) => generateQR(token))
      .catch((err) => {
        console.warn('Fallback to current auth token:', err);
        if (jwt) generateQR(jwt);
        else {
          setError(err.message || 'Could not fetch token');
          setLoading(false);
        }
      });
  }, [authUser]);

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Cashier ID Card — ${authUser.name}</title>
      <style>
        body { margin: 0; padding: 40px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; display: flex; justify-content: center; background: #f8fafc; }
        .badge { width: 280px; background: white; border-radius: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; text-align: center; }
        .header { background: linear-gradient(135deg, #1e293b, #0f172a); color: white; padding: 18px 12px; }
        .header h3 { margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1.5px; opacity: 0.8; }
        .header h1 { margin: 4px 0 0; font-size: 18px; font-weight: 800; }
        .role { display: inline-block; background: rgba(59, 130, 246, 0.2); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.4); padding: 3px 12px; border-radius: 99px; font-size: 10px; font-weight: 800; margin-top: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
        .qr-area { padding: 20px; background: #ffffff; }
        .qr-area img { width: 180px; height: 180px; border-radius: 12px; border: 2px dashed #cbd5e1; padding: 6px; }
        .footer { padding: 12px; background: #f8fafc; border-top: 1px solid #f1f5f9; font-size: 9px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
      </style></head><body>
      <div class="badge">
        <div class="header">
          <h3>CloudAIRambo POS</h3>
          <h1>${authUser.name}</h1>
          <div class="role">${(authUser.role || 'staff').toUpperCase()}</div>
        </div>
        <div class="qr-area">
          <img src="${qrDataUrl}" />
        </div>
        <div class="footer">Scan to Authorize Terminal</div>
      </div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="relative overflow-hidden bg-white rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-6 sm:p-8">
      {/* Background Ambient Glow */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-72 h-72 bg-gradient-to-br from-indigo-400/20 to-blue-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 bg-gradient-to-tr from-cyan-400/15 to-violet-500/15 rounded-full blur-3xl pointer-events-none" />

      {/* Card Header */}
      <div className="relative flex items-center justify-between pb-6 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-md shadow-indigo-200 text-lg">
            🪪
          </div>
          <div>
            <h2 className="text-sm font-black text-slate-800 tracking-tight">Digital Cashier Pass</h2>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">Contactless scan authentication for POS registers</p>
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Active Key
        </span>
      </div>

      {/* Futuristic Pass View */}
      <div className="relative my-6 flex justify-center">
        <div className="w-full max-w-xs bg-gradient-to-b from-slate-900 via-slate-800 to-slate-950 rounded-3xl p-5 shadow-2xl shadow-indigo-950/30 text-white border border-slate-700/60 flex flex-col items-center text-center relative overflow-hidden group">
          {/* Lanyard Hole Clip Graphic */}
          <div className="w-10 h-2 bg-slate-950 rounded-full border border-slate-700/80 mb-4 shadow-inner" />

          {/* User Info */}
          <div className="space-y-1">
            <h3 className="text-base font-black tracking-tight text-white">{authUser.name}</h3>
            <p className="text-[10px] text-slate-400 font-mono tracking-tight truncate max-w-[200px]">{authUser.email}</p>
            <div className="pt-1">
              <span className="inline-block px-3 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 backdrop-blur-sm">
                {authUser.role || 'Cashier'}
              </span>
            </div>
          </div>

          {/* QR Container with High-Tech Border */}
          <div className="mt-5 p-3 rounded-2xl bg-white shadow-xl relative group-hover:scale-105 transition-transform duration-300">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="Cashier QR"
                className="w-44 h-44 rounded-xl object-contain block"
              />
            ) : error ? (
              <div className="w-44 h-44 rounded-xl bg-red-50 text-red-600 flex flex-col items-center justify-center p-3 text-center border border-red-200">
                <span className="text-2xl mb-1">⚠️</span>
                <span className="text-[10px] font-bold leading-tight">{error}</span>
              </div>
            ) : (
              <div className="w-44 h-44 rounded-xl bg-slate-50 flex flex-col items-center justify-center gap-2">
                <div className="w-7 h-7 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Generating...</span>
              </div>
            )}
          </div>

          {/* Micro Footer on Card */}
          <div className="mt-5 flex items-center justify-between w-full text-[9px] font-mono text-slate-400 px-2 tracking-wider">
            <span>CLOUDAIRAMBO</span>
            <span className="text-emerald-400 font-bold">● READY</span>
          </div>
        </div>
      </div>

      {/* Safety Notice */}
      <div className="relative flex items-center gap-2.5 p-3 rounded-2xl bg-amber-50/80 border border-amber-200/80 text-amber-800 text-[11px] font-medium mb-5">
        <span className="text-base shrink-0">🛡️</span>
        <p className="leading-snug">
          This QR holds your unique cryptographic terminal token. Keep it safe and do not share on public channels.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <button
          onClick={handlePrint}
          disabled={!qrDataUrl || loading}
          className="py-3 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-bold text-xs tracking-wide shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
        >
          <span>🖨️</span> Print Pass
        </button>

        <button
          disabled={!qrDataUrl || loading}
          onClick={async () => {
            if (!qrDataUrl) return;
            const t = localStorage.getItem('cloudairambo_jwt') || '';
            const rawInput = prompt('Enter WhatsApp phone number with country code (e.g. 919876543210):');
            if (!rawInput) return;
            const cleanDigits = rawInput.replace(/\D/g, '');
            if (cleanDigits.length < 7) {
              alert('Please enter a valid phone number with country code.');
              return;
            }
            const phone = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;

            try {
              const res = await fetch(`${BACKEND}/whatsapp/send-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
                body: JSON.stringify({
                  phone,
                  image: qrDataUrl,
                  caption: `🪪 Cashier Pass — ${authUser.name}\nRole: ${(authUser.role || 'staff').toUpperCase()}\n\nScan at the POS terminal screen to sign in instantly.`,
                }),
              });
              const data = await res.json();
              if (data.success) alert('✅ Cashier pass sent via WhatsApp!');
              else alert('Failed: ' + (data.error || 'unknown'));
            } catch (e: any) {
              alert('WhatsApp error: ' + e.message);
            }
          }}
          className="py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold text-xs tracking-wide shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
        >
          <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          Send WhatsApp
        </button>

        <button
          disabled={!qrDataUrl || loading}
          onClick={() => {
            const subject = `Cashier Pass — ${authUser.name}`;
            const body = `Hi ${authUser.name},\n\nAttached is your CloudAIRambo POS cashier pass.\n\nRole: ${(authUser.role || 'staff').toUpperCase()}\nScan this QR code with the POS terminal camera or scanner to log in instantly.\n\nDo not share this QR with anyone.`;
            window.open(`mailto:${authUser.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
          }}
          className="py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold text-xs tracking-wide shadow-md shadow-blue-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
        >
          <span>✉️</span> Email Pass
        </button>
      </div>
    </div>
  );
}




function ReferAndEarnCard({ authUser }: { authUser: AuthUser }) {
  const [data, setData] = useState<{
    referral_code: string;
    referral_link: string;
    program_enabled: boolean;
    reward_percent: number | null;
    reward_flat_amount: number | null;
    reward_currency: string | null;
  } | null>(null);
  const [stats, setStats] = useState<{ total_referred: number; converted: number; pending: number; total_credit_earned: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${BACKEND}/referrals/me`, { headers }).then(r => r.json()).then(setData).catch(() => {});
    fetch(`${BACKEND}/referrals/stats`, { headers }).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const handleCopy = () => {
    if (!data) return;
    navigator.clipboard.writeText(data.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100 text-base">🎁</div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">Refer & Earn</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Invite a business, earn account credit when they subscribe</p>
        </div>
      </div>

      {data && !data.program_enabled ? (
        <div className="flex flex-col items-center gap-1.5 py-6 text-center">
          <span className="text-2xl">🔒</span>
          <p className="text-xs font-extrabold text-slate-600">Refer & Earn is currently paused</p>
          <p className="text-[10px] text-slate-400 max-w-xs">Any referrals already awaiting reward will still be honored.</p>
        </div>
      ) : (
      <>
      {data && (data.reward_percent || data.reward_flat_amount) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <p className="text-[11px] font-bold text-emerald-700">
            {data.reward_flat_amount
              ? `Earn ${data.reward_currency ?? ''} ${data.reward_flat_amount} per successful referral`
              : `Earn ${data.reward_percent}% of their first subscription payment`}
          </p>
        </div>
      )}

      {data && (
        <div className="flex items-center gap-2 bg-slate-900 rounded-xl px-4 py-3">
          <span className="font-mono text-xs text-green-400 flex-1 truncate select-all">{data.referral_link}</span>
          <button onClick={handleCopy} className="shrink-0 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-bold text-xs transition-all">
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-xl font-black text-slate-800">{stats.total_referred}</p>
            <p className="text-[9px] text-slate-400 font-bold uppercase">Referred</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
            <p className="text-xl font-black text-emerald-700">{stats.converted}</p>
            <p className="text-[9px] text-emerald-500 font-bold uppercase">Converted</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-xl font-black text-amber-700">{stats.total_credit_earned}</p>
            <p className="text-[9px] text-amber-500 font-bold uppercase">Credit Earned{data?.reward_currency ? ` (${data.reward_currency})` : ''}</p>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[10px] text-blue-700 font-medium">
        💡 Reward is credited only after your referral has stayed subscribed for a full month — see terms below.
      </div>

      <div className="border-t border-slate-100 pt-3 space-y-2">
        <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Terms & Conditions</p>
        <ul className="space-y-1.5 text-[10px] text-slate-500 leading-relaxed">
          <li className="flex gap-1.5"><span className="text-emerald-500 shrink-0">•</span>The person you refer must sign up using <strong>your referral link</strong>. Signups without the link cannot be linked to you afterward.</li>
          <li className="flex gap-1.5"><span className="text-emerald-500 shrink-0">•</span>Your referral must <strong>subscribe to a paid plan or module</strong> (Suite or any standalone module) — free trials alone don't qualify.</li>
          <li className="flex gap-1.5"><span className="text-emerald-500 shrink-0">•</span>Reward is credited only after their subscription has stayed active for a <strong>full 30 days</strong> from their first payment.</li>
          <li className="flex gap-1.5"><span className="text-red-500 shrink-0">•</span>If they <strong>cancel or get refunded within those 30 days</strong>, no reward is credited — the referral is voided.</li>
          <li className="flex gap-1.5"><span className="text-emerald-500 shrink-0">•</span>Each referred person can only be linked to <strong>one</strong> referrer, and only counts once.</li>
          <li className="flex gap-1.5"><span className="text-emerald-500 shrink-0">•</span>Self-referrals aren't allowed.</li>
          <li className="flex gap-1.5"><span className="text-amber-500 shrink-0">•</span>Reward amount/percentage may vary by country. If it's a percentage, it's calculated on your referral's actual first subscription payment — whichever plan or module they choose — and is locked in at the time they pay.</li>
          <li className="flex gap-1.5"><span className="text-slate-400 shrink-0">•</span>CloudAIRambo may modify or discontinue the Refer & Earn program at any time. Referrals already awaiting their 30-day reward at the time of a change will still be honored per these terms.</li>
        </ul>
      </div>
      </>
      )}
    </div>
  );
}





function OrgApiKeyCard() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [copied, setCopied] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${BACKEND}/auth/org/api-key`, { headers })
      .then(r => r.json())
      .then(d => { setApiKey(d.api_key); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleRegen = async () => {
    if (!confirm('Regenerate API key? The old key will stop working immediately.')) return;
    setRegen(true);
    const res = await fetch(`${BACKEND}/auth/org/api-key/regenerate`, { method: 'POST', headers });
    const d = await res.json();
    setApiKey(d.api_key);
    setRegen(false);
  };

  const handleCopy = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center border border-violet-100 text-base">🔑</div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">API Key</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Used to display payment QR codes, payment links, and external integrations — keep it private</p>
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-slate-400 py-4 text-center">Loading...</div>
      ) : (
        <>
          <div className="flex items-center gap-2 bg-slate-900 rounded-xl px-4 py-3">
            <span className="font-mono text-sm text-green-400 flex-1 truncate select-all">{apiKey}</span>
            <button onClick={handleCopy} className="shrink-0 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-bold text-xs transition-all">
              {copied ? '✓ Copied' : '📋 Copy'}
            </button>
          </div>
          <div className="space-y-2">
  <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-1.5">
    <p className="text-[11px] font-black text-red-700">🚨 Sensitive Key — Read Before Sharing</p>
    <p className="text-[10px] text-red-600 font-medium">This key grants access to your organization's payment display screen, payment QR codes, and public-facing payment links. If exposed, anyone can impersonate your payment page.</p>
  </div>
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1.5">
    <p className="text-[10px] font-black text-amber-700">⚠ Safety Tips</p>
    <ul className="text-[10px] text-amber-700 space-y-1 list-none">
      <li>• Never share this key publicly or in chat groups</li>
      <li>• Do not paste it in emails or screenshots</li>
      <li>• Only paste it in trusted CloudAIRambo-connected devices</li>
      <li>• If you suspect it's been exposed, regenerate immediately</li>
      <li>• Regenerating will disconnect all devices using the old key</li>
    </ul>
  </div>
</div>
          <button onClick={handleRegen} disabled={regen} className="w-full py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-xl font-bold text-xs transition-all disabled:opacity-50">
            {regen ? 'Regenerating...' : '↺ Regenerate Key'}
          </button>
        </>
      )}
    </div>
  );
}

function POSCountersCard({ token, authUser }: { token: string; authUser?: AuthUser }) {
  const [counters, setCounters] = useState<{ prefix: string; counter: number }[]>([]);
  const [outlets, setOutlets] = useState<{ id: number; name: string; invoice_prefix?: string | null }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [cfgRes, allRes, outletsRes] = await Promise.all([
          fetch(`${BACKEND}/invoices/number-config`, { headers }),
          fetch(`${BACKEND}/invoices/all-prefixes`, { headers }),
          fetch(`${BACKEND}/outlets`, { headers }),
        ]);
        const all: { prefix: string; counter: number }[] = await allRes.json();
        const outletsData = await outletsRes.json();
        setOutlets(Array.isArray(outletsData) ? outletsData : []);
        // Show all counters for this org — org-wide prefix AND any
        // outlet-specific prefixes. The old filter only kept counters
        // starting with the org prefix, which silently hid every
        // outlet-scoped counter (e.g. MUM-REC) once those existed.
        setCounters(all);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const renderCounterCard = (c: { prefix: string; counter: number }) => {
    const issued = c.counter - 1;
    const next = `${c.prefix}-${String(c.counter).padStart(5, '0')}`;
    return (
      <div key={c.prefix} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-w-[160px]">
        <p className="text-xs font-medium text-slate-500">{c.prefix}</p>
        <p className="text-3xl font-bold text-slate-800 mt-1">{issued}</p>
        <p className="text-xs text-slate-400">issued</p>
        {issued > 0 ? (
          <p className="text-xs text-purple-500 mt-1 font-mono">Next: {next}</p>
        ) : (
          <p className="text-xs text-slate-300 mt-1">Not used yet</p>
        )}
      </div>
    );
  };

  // Group counters: one bucket per outlet whose invoice_prefix matches this
  // counter's prefix, plus a "Shared" bucket for anything left over (org-wide
  // counters, or outlets that don't have their own prefix set).
  const usedPrefixes = new Set<string>();
  const outletGroups = outlets
    .filter(o => o.invoice_prefix)
    .map(o => {
      const outletPrefix = o.invoice_prefix!.toUpperCase();
      const matches = counters.filter(c => c.prefix.startsWith(`${outletPrefix}-`));
      matches.forEach(c => usedPrefixes.add(c.prefix));
      return { outlet: o, counters: matches };
    })
    .filter(g => g.counters.length > 0);

  const sharedCounters = counters.filter(c => !usedPrefixes.has(c.prefix));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 border border-purple-100">🧾</div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">Serial Counters</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Backend-assigned numbers per document type. Auto-increments per invoice.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : counters.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No counters found. Save a receipt first.</p>
      ) : (
        <div className="space-y-4">
          {outletGroups.map(({ outlet, counters: outletCounters }) => (
            <div key={outlet.id} className="space-y-2">
              <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">🏪 {outlet.name}</p>
              <div className="flex flex-wrap gap-3">
                {outletCounters.map(renderCounterCard)}
              </div>
            </div>
          ))}
          {sharedCounters.length > 0 && (
            <div className="space-y-2">
              {outletGroups.length > 0 && (
                <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Shared / Org-wide</p>
              )}
              <div className="flex flex-wrap gap-3">
                {sharedCounters.map(renderCounterCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function OutletWhatsAppCard({ outlet }: { outlet: { id: number; name: string; city: string | null; whatsapp_port: number | null; invoice_prefix: string | null; whatsapp_number?: string | null } }) {
  const [status, setStatus] = useState<'loading' | 'disconnected' | 'qr_pending' | 'connected' | 'wrong_number'>('loading');
  const [qr, setQr] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [wrongNumber, setWrongNumber] = useState<string | null>(null);
  const [expectedNumber, setExpectedNumber] = useState<string | null>(null);
  const startedRef = useRef(false);

  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${BACKEND}/whatsapp/status?outlet_id=${outlet.id}`, { headers });
      const data = await res.json();
      setStatus(data.status);
      setPhone(data.phone || null);
      setProfileName(data.profile_name || null);
      setWrongNumber(data.wrong_number || null);
      setExpectedNumber(data.expected_number || null);
      if (data.status === 'qr_pending') {
        const qrRes = await fetch(`${BACKEND}/whatsapp/qr?outlet_id=${outlet.id}`, { headers });
        const qrData = await qrRes.json();
        if (qrData.qr) setQr(qrData.qr);
      }
      if (data.status === 'connected') { setQr(null); startedRef.current = false; }
      if (data.status === 'disconnected' && !startedRef.current) {
  startedRef.current = true;
  await fetch(`${BACKEND}/whatsapp/session/start?outlet_id=${outlet.id}`, {
    method: 'POST', headers,
    body: JSON.stringify({ expected_number: outlet.whatsapp_number || null }),
  });
}
    } catch { setStatus('disconnected'); }
  };

  const startSession = async () => {
    try {
      await fetch(`${BACKEND}/whatsapp/session/start?outlet_id=${outlet.id}`, {
        method: 'POST', headers,
        body: JSON.stringify({ expected_number: outlet.whatsapp_number || null }),
      });
    } catch {}
  };

  useEffect(() => {
    fetchStatus();
    // Fast poll while waiting (1s), slow once connected (10s)
    const interval = setInterval(fetchStatus, status === 'connected' ? 10000 : 1000);
    return () => clearInterval(interval);
  }, [outlet.id, status]);

  const handleLogout = async () => {
    if (!confirm(`Disconnect WhatsApp for ${outlet.name}?`)) return;
    await fetch(`${BACKEND}/whatsapp/logout?outlet_id=${outlet.id}`, { method: 'POST', headers });
    startedRef.current = false;
    setStatus('disconnected'); setQr(null);
  };

  const port = outlet.whatsapp_port || 3001;

  return (
    <div style={{ background: '#fff', border: `1.5px solid ${status === 'connected' ? '#86efac' : '#e5e7eb'}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🏪</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{outlet.name}</div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>
              {outlet.city && `${outlet.city} · `}Port {port}
              {outlet.invoice_prefix && ` · Prefix: ${outlet.invoice_prefix}`}
            </div>
          </div>
        </div>
        <div style={{
          fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
          background: status === 'connected' ? '#f0fdf4' : status === 'qr_pending' ? '#fffbeb' : '#f8fafc',
          color: status === 'connected' ? '#16a34a' : status === 'qr_pending' ? '#d97706' : '#9ca3af',
          border: `1px solid ${status === 'connected' ? '#86efac' : status === 'qr_pending' ? '#fde68a' : '#e5e7eb'}`,
        }}>
          {status === 'connected' ? '● Connected' : status === 'qr_pending' ? '◌ Scan QR' : status === 'loading' ? '...' : '○ Disconnected'}
        </div>
      </div>

      {status === 'qr_pending' && qr && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center' }}>Open WhatsApp → Linked Devices → Link a Device → scan</div>
          <img src={qr} alt="QR" style={{ width: 160, height: 160, borderRadius: 10, border: '2px solid #86efac' }} />
        </div>
      )}

      {status === 'connected' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>✅ Connected</div>
            {phone && (
              <div style={{ fontSize: 11, color: '#374151', fontWeight: 700, marginTop: 2 }}>
                +{phone} {profileName ? `· ${profileName}` : ''}
              </div>
            )}
          </div>
          <button onClick={handleLogout} style={{ fontSize: 11, color: '#dc2626', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Disconnect</button>
        </div>
      )}




      {status === 'wrong_number' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>❌ Wrong number scanned</div>
          <div style={{ fontSize: 11, color: '#991b1b' }}>
            This outlet is locked to <strong>+{expectedNumber}</strong>, but you scanned <strong>+{wrongNumber}</strong>.
          </div>
          <div style={{ fontSize: 10, color: '#b91c1c', marginTop: 4 }}>
            Please scan QR with the correct phone. Session was disconnected automatically.
          </div>
        </div>
      )}

      {status === 'disconnected' && (
        <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', padding: '4px 0' }}>
          <div>Reconnecting... QR will appear shortly.</div>
          <div style={{ fontSize: 10, marginTop: 4, color: '#d1d5db' }}>Auto-refreshing every 10s</div>
        </div>
      )}
    </div>
  );
}

function OutletGmailCard({ outlet, orgFallbackEmail }: { outlet: { id: number; name: string; city: string | null; gmail_connected_email?: string | null }; orgFallbackEmail: string | null }) {
  const [connecting, setConnecting] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const isConnected = !!outlet.gmail_connected_email;

  const handleConnect = () => {
    setConnecting(true);
    window.location.href = `${BACKEND}/auth/gmail-connect-outlet?outlet_id=${outlet.id}&user_token=${token}`;
  };

  const handleDisconnect = async () => {
    if (!confirm(`Disconnect Gmail for ${outlet.name}?`)) return;
    await fetch(`${BACKEND}/auth/gmail-disconnect-outlet?outlet_id=${outlet.id}`, { method: 'POST', headers });
    window.location.reload();
  };

  return (
    <div style={{ background: '#fff', border: `1.5px solid ${isConnected ? '#86efac' : '#e5e7eb'}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isConnected || !orgFallbackEmail ? 10 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✉️</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{outlet.name}</div>
            {outlet.city && <div style={{ fontSize: 10, color: '#9ca3af' }}>{outlet.city}</div>}
          </div>
        </div>
        <div style={{
          fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
          background: isConnected ? '#f0fdf4' : '#f8fafc',
          color: isConnected ? '#16a34a' : '#9ca3af',
          border: `1px solid ${isConnected ? '#86efac' : '#e5e7eb'}`,
        }}>
          {isConnected ? '● Connected' : '○ Using default'}
        </div>
      </div>

      {isConnected ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, color: '#374151', fontWeight: 700 }}>{outlet.gmail_connected_email}</div>
          <button onClick={handleDisconnect} style={{ fontSize: 11, color: '#dc2626', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Disconnect</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>
            {orgFallbackEmail ? `Falls back to: ${orgFallbackEmail}` : 'No org default connected either'}
          </div>
          <button onClick={handleConnect} disabled={connecting} style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#dc2626', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap' as const }}>
            {connecting ? 'Redirecting…' : 'Connect Gmail'}
          </button>
        </div>
      )}
    </div>
  );
}

function OrgGmailCard({ orgEmail }: { orgEmail: string | null }) {
  const [connecting, setConnecting] = useState(false);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const handleConnect = () => {
    setConnecting(true);
    window.location.href = `${BACKEND}/auth/gmail-connect?user_token=${token}`;
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect the org-wide default Gmail?')) return;
    await fetch(`${BACKEND}/auth/gmail-disconnect`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    window.location.reload();
  };

  return (
    <div className={`bg-white rounded-2xl border shadow-sm p-4 transition-all ${orgEmail ? 'border-emerald-200' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center border ${orgEmail ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
            <Mail className={`w-4 h-4 ${orgEmail ? 'text-emerald-600' : 'text-slate-400'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-extrabold text-slate-800">Org-wide default</p>
            <p className="text-[10px] text-slate-400 font-medium">Used when a branch has no Gmail of its own</p>
          </div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border ${
          orgEmail ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${orgEmail ? 'bg-emerald-500' : 'bg-slate-300'}`} />
          {orgEmail ? 'Connected' : 'Not connected'}
        </span>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
        <p className={`text-[11px] font-mono truncate ${orgEmail ? 'text-slate-700 font-semibold' : 'text-slate-300'}`}>
          {orgEmail || 'No account connected'}
        </p>
        {orgEmail ? (
          <button
            onClick={handleDisconnect}
            className="shrink-0 text-[10px] font-bold text-red-500 hover:text-red-700 bg-white hover:bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg transition-all"
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="shrink-0 flex items-center gap-1.5 text-[10px] font-bold text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-60 px-3 py-1.5 rounded-lg transition-all"
          >
            <svg className="w-3 h-3" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            {connecting ? 'Redirecting…' : 'Connect Gmail'}
          </button>
        )}
      </div>
    </div>
  );
}

function OrgWideWhatsAppCard() {
  const [provider, setProvider] = useState<'baileys' | 'business_api'>('baileys');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center border border-green-100 text-lg">
          <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">Organization-wide WhatsApp</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Used automatically for any branch that hasn't connected its own WhatsApp number</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setProvider('baileys')}
          className={`flex-1 py-2 rounded-lg border-2 font-bold text-xs transition-all ${provider === 'baileys' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
        >
          📷 QR / Linked Device
        </button>
        <button
          onClick={() => setProvider('business_api')}
          className={`flex-1 py-2 rounded-lg border-2 font-bold text-xs transition-all ${provider === 'business_api' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
        >
          🏢 Business API
        </button>
      </div>

      {provider === 'baileys'
        ? <WhatsAppConnectCard outletId={null} />
        : <OrgWhatsAppBusinessAPICard />}
    </div>
  );
}

function OutletsGmailSection({ authUser, moduleStatuses, outletId }: { authUser: AuthUser | null; moduleStatuses?: Record<string, string>; outletId?: number | null }) {
  const perms = getPermissions(authUser?.role, (authUser as any)?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showOrgWideGmail, setShowOrgWideGmail] = useState(false);

  useEffect(() => {
    if (!authUser || !perms.canManageProfileMessaging) return;
    const token = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d)) return;
        setOutlets(d);
        const navMatch = outletId != null && d.some((o: any) => o.id === outletId);
        if (navMatch) setSelectedId(outletId);
        else if (d.length > 0) setSelectedId(d[0].id);
      })
      .catch(() => {});
  }, [authUser, perms.canManageProfileMessaging]);

  // Keep in sync whenever the navbar outlet changes
  useEffect(() => {
    if (outletId == null) return;
    if (outlets.some(o => o.id === outletId)) setSelectedId(outletId);
  }, [outletId, outlets]);

  if (!authUser || !perms.canManageProfileMessaging || outlets.length === 0) return null;

  const orgFallbackEmail = (authUser as any)?.gmail_connected_email || null;
  const selectedOutlet = outlets.find(o => o.id === selectedId);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center border border-red-100 text-lg">✉️</div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">Per-Outlet Gmail Sender</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Each branch can send invoices from its own Gmail — otherwise falls back to your account's default</p>
        </div>
      </div>
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-red-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}
      {selectedOutlet && <OutletGmailCard outlet={selectedOutlet} orgFallbackEmail={orgFallbackEmail} />}

      <button
        onClick={() => setShowOrgWideGmail(s => !s)}
        className="w-full py-2 text-[11px] font-bold text-slate-500 border border-dashed border-slate-300 rounded-xl hover:border-slate-400 hover:text-slate-700 transition-all"
      >
        {showOrgWideGmail ? '▲ Hide org-wide default Gmail' : '▼ Set an org-wide default (fallback) Gmail'}
      </button>
      {showOrgWideGmail && <OrgGmailCard orgEmail={orgFallbackEmail} />}
    </div>
  );
}

function OrgWhatsAppBusinessAPICard() {
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await fetch(`${BACKEND}/whatsapp/business-api/test`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone_number_id: phoneNumberId, access_token: accessToken, waba_id: wabaId }),
      });
      setTestResult(await res.json());
    } catch (e: any) {
      setTestResult({ valid: false, message: e.message });
    } finally { setTesting(false); }
  };

  const handleConnect = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BACKEND}/whatsapp/org/business-api`, {
        method: 'POST', headers,
        body: JSON.stringify({ phone_number_id: phoneNumberId, access_token: accessToken, waba_id: wabaId }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Connection failed'); return; }
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
      <p className="text-xs font-extrabold text-slate-800">WhatsApp Business API — Org-wide default</p>
      <p className="text-[9px] text-slate-400">Used as fallback for any outlet on Business API with no credentials of its own. Get these from Meta Business Suite → WhatsApp → API Setup.</p>
      {[
        { label: 'Phone Number ID', value: phoneNumberId, set: setPhoneNumberId },
        { label: 'WABA ID', value: wabaId, set: setWabaId },
      ].map(f => (
        <div key={f.label} className="space-y-1">
          <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">{f.label}</label>
          <input type="text" value={f.value} onChange={e => f.set(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono" />
        </div>
      ))}
      <div className="space-y-1">
        <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Permanent Access Token</label>
        <input type="password" value={accessToken} onChange={e => setAccessToken(e.target.value)}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono" />
      </div>
      {testResult && (
        <div className={`px-3 py-2 rounded-lg text-[10px] font-semibold border ${testResult.valid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {testResult.valid ? '✅' : '❌'} {testResult.message}
        </div>
      )}
      {error && (
        <div className="px-3 py-2 rounded-lg text-[10px] font-semibold border bg-red-50 border-red-200 text-red-700">
          ❌ {error}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={handleTest} disabled={testing || !phoneNumberId || !accessToken}
          className="flex-1 py-2 rounded-lg border border-green-300 text-green-700 font-bold text-xs">
          {testing ? 'Testing...' : '🔍 Test'}
        </button>
        <button onClick={handleConnect} disabled={saving || !phoneNumberId || !accessToken}
          className={`flex-1 py-2 rounded-lg font-bold text-xs text-white ${saved ? 'bg-emerald-600' : 'bg-green-600 hover:bg-green-700'}`}>
          {saved ? '✓ Connected' : saving ? 'Saving...' : 'Connect'}
        </button>
      </div>
    </div>
  );
}



function OutletsWhatsAppSection({ authUser, moduleStatuses, outletId }: { authUser: AuthUser | null; moduleStatuses?: Record<string, string>; outletId?: number | null }) {
  const perms = getPermissions(authUser?.role, (authUser as any)?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [outletPhones, setOutletPhones] = useState<Record<number, string | null>>({});
  const [providerChoice, setProviderChoice] = useState<Record<number, 'baileys' | 'business_api'>>({});
  const [showOrgWideWa, setShowOrgWideWa] = useState(false);
  const [orgProvider, setOrgProvider] = useState<'baileys' | 'business_api'>('baileys');

  useEffect(() => {
    if (!authUser || !perms.canManageProfileMessaging) return;
    const token = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d)) return;
        setOutlets(d);
        // Prefer the navbar-selected outlet if it's in the list, else fall back to the first one
        const navMatch = outletId != null && d.some((o: any) => o.id === outletId);
        if (navMatch) setSelectedId(outletId);
        else if (d.length > 0) setSelectedId(d[0].id);
      })
      .catch(() => {});
  }, [authUser, perms.canManageProfileMessaging]);

  // Keep this section's selected branch in sync whenever the navbar outlet changes
  useEffect(() => {
    if (outletId == null) return;
    if (outlets.some(o => o.id === outletId)) setSelectedId(outletId);
  }, [outletId, outlets]);

  useEffect(() => {
    if (outlets.length === 0) return;
    const token = localStorage.getItem('cloudairambo_jwt') || '';
    const headers = { Authorization: `Bearer ${token}` };
    const check = () => {
      outlets.forEach(o => {
        fetch(`${BACKEND}/whatsapp/status?outlet_id=${o.id}`, { headers })
          .then(r => r.json())
          .then(d => setOutletPhones(prev => ({ ...prev, [o.id]: d.phone || null })))
          .catch(() => {});
      });
    };
    check();
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, [outlets]);

  if (!authUser || !perms.canManageProfileMessaging || outlets.length === 0) return null;

  const phoneCounts: Record<string, number[]> = {};
  Object.entries(outletPhones).forEach(([oid, phone]) => {
    if (!phone) return;
    if (!phoneCounts[phone]) phoneCounts[phone] = [];
    phoneCounts[phone].push(Number(oid));
  });
  const duplicateGroups = Object.entries(phoneCounts).filter(([, ids]) => ids.length > 1);
  const selectedOutlet = outlets.find(o => o.id === selectedId);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center border border-green-100 text-lg">🏪</div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">Per-Outlet WhatsApp</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">Each branch connects its own WhatsApp number</p>
        </div>
      </div>

      {duplicateGroups.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', marginBottom: 4 }}>⚠ Same number used by multiple outlets</div>
          {duplicateGroups.map(([phone, ids]) => {
            const names = ids.map(id => outlets.find(o => o.id === id)?.name || `Outlet ${id}`).join(', ');
            return (
              <div key={phone} style={{ fontSize: 11, color: '#92400e' }}>
                +{phone} is linked to: <strong>{names}</strong>
              </div>
            );
          })}
        </div>
      )}

      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-green-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}

      {selectedOutlet && (() => {
        const currentProvider = providerChoice[selectedOutlet.id] || selectedOutlet.whatsapp_provider || 'baileys';
        return (
          <>
            <div className="flex gap-2">
              <button
                onClick={() => setProviderChoice(p => ({ ...p, [selectedOutlet.id]: 'baileys' }))}
                className={`flex-1 py-2 rounded-lg border-2 font-bold text-xs transition-all ${currentProvider === 'baileys' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
              >
                📷 QR / Linked Device
              </button>
              <button
                onClick={() => setProviderChoice(p => ({ ...p, [selectedOutlet.id]: 'business_api' }))}
                className={`flex-1 py-2 rounded-lg border-2 font-bold text-xs transition-all ${currentProvider === 'business_api' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
              >
                🏢 Business API
              </button>
            </div>
            {currentProvider === 'baileys'
              ? <OutletWhatsAppCard key={selectedOutlet.id} outlet={selectedOutlet} />
              : <OutletWhatsAppBusinessAPICard key={selectedOutlet.id} outlet={selectedOutlet} />}
          </>
        );
      })()}

      <button
        onClick={() => setShowOrgWideWa(s => !s)}
        className="w-full py-2 text-[11px] font-bold text-slate-500 border border-dashed border-slate-300 rounded-xl hover:border-slate-400 hover:text-slate-700 transition-all"
      >
        {showOrgWideWa ? '▲ Hide org-wide default WhatsApp' : '▼ Set an org-wide default (fallback) WhatsApp'}
      </button>
      {showOrgWideWa && (
        <div className="space-y-2">
          <p className="text-[10px] text-slate-400">
            Used automatically for any branch that hasn't connected its own WhatsApp number.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setOrgProvider('baileys')}
              className={`flex-1 py-2 rounded-lg border-2 font-bold text-xs transition-all ${orgProvider === 'baileys' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
            >
              📷 QR / Linked Device
            </button>
            <button
              onClick={() => setOrgProvider('business_api')}
              className={`flex-1 py-2 rounded-lg border-2 font-bold text-xs transition-all ${orgProvider === 'business_api' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
            >
              🏢 Business API
            </button>
          </div>
          {orgProvider === 'baileys'
            ? <WhatsAppConnectCard outletId={null} />
            : <OrgWhatsAppBusinessAPICard />}
        </div>
      )}
    </div>
  );
}
function WhatsAppConnectCard({ outletId }: { outletId: number | null }) {
  const [status, setStatus] = useState<'loading' | 'disconnected' | 'qr_pending' | 'connected'>('loading');
  const [qr, setQr] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [phone, setPhone] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [outletName, setOutletName] = useState<string | null>(null);
  const startedRef = useRef(false);

  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (outletId == null) { setOutletName(null); return; }
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const match = Array.isArray(d) ? d.find((o: any) => o.id === outletId) : null;
        setOutletName(match ? match.name : null);
      })
      .catch(() => {});
  }, [outletId]);

    const fetchStatus = async () => {
  try {
    const url = outletId != null
      ? `${BACKEND}/whatsapp/status?outlet_id=${outletId}`
      : `${BACKEND}/whatsapp/org/status`;
    const res = await fetch(url, { headers });
    const data = await res.json();

    setStatus(data.status);
    setPhone(data.phone || null);
    setProfileName(data.profile_name || null);
    if (data.status === 'qr_pending') {
      fetchQR();
    }
    if (data.status === 'connected') { setQr(null); startedRef.current = false; }
    if (data.status === 'disconnected' && !startedRef.current) {
      startedRef.current = true;
      const startUrl = outletId != null
        ? `${BACKEND}/whatsapp/session/start?outlet_id=${outletId}`
        : `${BACKEND}/whatsapp/org/session/start`;
      await fetch(startUrl, { method: 'POST', headers });
    }
  } catch (e) {
    setStatus('disconnected');
  }
};

  const fetchQR = async () => {
    try {
      const url = outletId != null
        ? `${BACKEND}/whatsapp/qr?outlet_id=${outletId}`
        : `${BACKEND}/whatsapp/org/qr`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (data.qr) setQr(data.qr);
    } catch {}
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => {
      fetchStatus();
    }, status === 'connected' ? 8000 : 1000);
    return () => clearInterval(interval);
  }, [status, outletId]);

   const handleLogout = async () => {
    if (!confirm('Disconnect WhatsApp?')) return;
    const url = outletId != null
      ? `${BACKEND}/whatsapp/logout?outlet_id=${outletId}`
      : `${BACKEND}/whatsapp/org/logout`;
    await fetch(url, { method: 'POST', headers });
    startedRef.current = false;
    setStatus('disconnected');
    setQr(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center border border-green-100">
            <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-extrabold text-slate-800">
              WhatsApp Auto-Send
              {outletId != null && (
                <span className="ml-1.5 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full align-middle">
                  🏪 {outletName || `Outlet #${outletId}`}
                </span>
              )}
              {outletId == null && (
                <span className="ml-1.5 text-[10px] font-bold text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-full align-middle">
                  Org-wide default
                </span>
              )}
            </h2>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">Scan once — messages send automatically forever</p>
          </div>
        </div>
        <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${
          status === 'connected' ? 'bg-green-50 text-green-700 border-green-200' :
          status === 'qr_pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
          'bg-slate-50 text-slate-500 border-slate-200'
        }`}>
          {status === 'connected' ? '● Connected' : status === 'qr_pending' ? '◌ Scan QR' : status === 'loading' ? '...' : '○ Disconnected'}
        </div>
      </div>

      {status === 'loading' && (
        <div className="text-center py-6 text-slate-400 text-xs">Checking connection...</div>
      )}

      {status === 'disconnected' && (
        <div className="text-center py-4 space-y-3">
          <p className="text-xs text-slate-500">WhatsApp not connected. The service may be starting up — wait a few seconds and it will show a QR code.</p>
          <div className="text-slate-300 text-xs">Auto-refreshing...</div>
        </div>
      )}

      {status === 'qr_pending' && !qr && (
        <div className="text-center py-4 space-y-2">
          <div className="flex justify-center">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-xs text-slate-500">QR loading...</p>
        </div>
      )}

      {status === 'qr_pending' && qr && (
        <div className="flex flex-col items-center space-y-3">
          <p className="text-xs text-slate-600 font-medium text-center">Open WhatsApp on your phone → tap <strong>⋮ Menu → Linked Devices → Link a Device</strong> → scan this QR</p>
          <div style={{ position: 'relative' }}>
            <img src={qr} alt="WhatsApp QR" className="w-52 h-52 border-2 border-green-200 rounded-xl shadow-sm" />
            <div style={{ position: 'absolute', inset: 0, borderRadius: '0.75rem', background: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid rgba(34,197,94,0.3)', borderTopColor: '#22c55e', animation: 'spin 1s linear infinite', margin: '0 auto 6px' }} />
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Waiting for scan...</div>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-slate-400">Will detect instantly once scanned</p>
        </div>
      )}

      {status === 'connected' && (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <div className="text-2xl mb-1">✅</div>
            <p className="text-sm font-bold text-green-800">WhatsApp Connected!</p>
            {phone && (
              <p className="text-xs font-bold text-slate-700 mt-1">
                +{phone} {profileName ? `· ${profileName}` : ''}
              </p>
            )}
            <p className="text-[10px] text-green-600 mt-1">All ledger charges, payments, and reminders will now send automatically to customers.</p>
          </div>
          <button onClick={handleLogout} className="w-full py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-xl font-bold text-xs transition-all">
            Disconnect WhatsApp
          </button>
        </div>
      )}
    </div>
  );
}

function OutletAttendanceDeviceCard({ outlet }: { outlet: { id: number; name: string; city: string | null } }) {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showGuide, setShowGuide] = useState(true);

  const token = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const backendHost = (BACKEND || 'https://api.cloudairambo.com').replace(/^https?:\/\//, '').replace(/\/api\/?$/, '');

  const fetchExisting = async () => {
    try {
      const res = await fetch(`${BACKEND}/subscriptions/kiosk-devices?module=employees`, { headers });
      const devices = await res.json();
      const match = Array.isArray(devices) ? devices.find((d: any) => d.outlet_id === outlet.id) : null;
      setApiKey(match ? match.api_key : null);
    } catch {}
  };

  useEffect(() => { fetchExisting(); }, [outlet.id]);

  const generateKey = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/subscriptions/kiosk-devices/generate-key`, {
        method: 'POST', headers,
        body: JSON.stringify({ outlet_id: outlet.id, module: 'employees', label: outlet.name }),
      });
      const d = await res.json();
      if (d.api_key) setApiKey(d.api_key);
    } catch {}
    setLoading(false);
  };

  const copyKey = () => {
    if (apiKey) { navigator.clipboard.writeText(apiKey); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  return (
    <div style={{ background: '#fff', border: `1.5px solid ${apiKey ? '#86efac' : '#e5e7eb'}`, borderRadius: 16, padding: '16px 20px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🖥️</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{outlet.name}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {outlet.city && `${outlet.city} · `}Outlet #{outlet.id}
            </div>
          </div>
        </div>
        <div style={{
          fontSize: 10, fontWeight: 800, padding: '4px 12px', borderRadius: 99,
          background: apiKey ? '#f0fdf4' : '#f8fafc',
          color: apiKey ? '#16a34a' : '#94a3b8',
          border: `1px solid ${apiKey ? '#86efac' : '#e2e8f0'}`,
        }}>
          {apiKey ? '● Device Key Active' : '○ No Key'}
        </div>
      </div>

      {apiKey ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Key Box */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              flex: 1,
              background: '#f8fafc',
              border: '1.5px solid #e2e8f0',
              borderRadius: 10, padding: '10px 14px',
              fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
              color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {apiKey}
            </div>
            <button onClick={copyKey} style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#0f172a', border: 'none', borderRadius: 10, padding: '10px 16px', cursor: 'pointer' }}>
              {copied ? '✓ Copied' : '📋 Copy Key'}
            </button>
            <button onClick={generateKey} disabled={loading} style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', cursor: 'pointer' }}>
              ↺ Regen
            </button>
          </div>

          {/* Collapsible Setup Documentation Card */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>📖 ZKTeco Machine Setup Guide</span>
              <button onClick={() => setShowGuide(g => !g)} style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
                {showGuide ? 'Hide Guide ▲' : 'Show Guide ▼'}
              </button>
            </div>

            {showGuide && (
              <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontSize: 11, color: '#475569', margin: 0, lineHeight: 1.5 }}>
                  Enter these exact parameters in your physical ZKTeco machine menu (<strong>Comm ➔ Cloud Server / ADMS / Web Server</strong>):
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Server Address</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', marginTop: 2 }}>{backendHost}</div>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Server Port</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', marginTop: 2 }}>443 <span style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>(or 80 for HTTP)</span></div>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Enable HTTPS / SSL</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>ON (Enabled)</div>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Push Path / URL</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', marginTop: 2 }}>/subscriptions/kiosk-devices/attendance-push</div>
                  </div>
                </div>

                <div style={{ fontSize: 11, color: '#334155', lineHeight: 1.6, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                  <strong>Employee ID Mapping:</strong> On the machine keypad, set each employee's <strong>User ID</strong> to their CloudAIRambo Employee ID (e.g. <code>33</code>). When they punch in, it streams live to this outlet automatically!
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <button onClick={generateKey} disabled={loading} style={{
          width: '100%', padding: '12px', borderRadius: 10, border: 'none',
          background: 'linear-gradient(135deg, #0f172a, #1e293b)',
          color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', opacity: loading ? 0.6 : 1,
        }}>
          {loading ? 'Generating...' : '🔑 Generate Key & Setup Guide for this Outlet'}
        </button>
      )}
    </div>
  );
}
function OutletGmailStatusCard({ outletId, authUser }: { outletId: number | null; authUser: AuthUser | null }) {
  const [outlet, setOutlet] = useState<{ id: number; name: string; gmail_connected_email?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const fetchOutlet = () => {
    if (!outletId) { setOutlet(null); setLoading(false); return; }
    setLoading(true);
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const match = Array.isArray(d) ? d.find((o: any) => o.id === outletId) : null;
        setOutlet(match || null);
      })
      .catch(() => setOutlet(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOutlet(); }, [outletId]);

  const handleConnect = () => {
    if (!outletId) return;
    window.location.href = `${BACKEND}/auth/gmail-connect-outlet?outlet_id=${outletId}&user_token=${token}`;
  };

  const handleDisconnect = async () => {
    if (!outletId) return;
    if (!confirm(`Disconnect Gmail for this branch?`)) return;
    await fetch(`${BACKEND}/auth/gmail-disconnect-outlet?outlet_id=${outletId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchOutlet();
  };

  if (!outletId) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-[10px] text-slate-400">
        No branch selected — pick a branch from the top navbar to see its Gmail sender status.
      </div>
    );
  }

  if (loading) {
    return <div className="text-[10px] text-slate-400 py-2">Loading branch Gmail status...</div>;
  }

  const isConnected = !!outlet?.gmail_connected_email;

  return isConnected ? (
    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-2.5">
      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-emerald-800">Gmail Connected — {outlet?.name}</p>
        <p className="text-[9px] font-mono text-emerald-600 truncate">{outlet?.gmail_connected_email}</p>
      </div>
      <button
        onClick={handleDisconnect}
        className="shrink-0 text-[9px] font-bold text-red-500 hover:text-red-700 bg-white border border-red-200 px-2 py-1 rounded-lg"
      >
        Change
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-red-700">Not connected — {outlet?.name || 'this branch'}</p>
        <p className="text-[9px] text-red-500">This branch has no Gmail of its own yet.</p>
      </div>
      <button
        onClick={handleConnect}
        className="shrink-0 text-[9px] font-bold text-white bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded-lg"
      >
        Connect Gmail
      </button>
    </div>
  );
}
function OutletsAttendanceSection({ authUser, outletId }: { authUser: AuthUser | null; outletId?: number | null }) {
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!authUser || !['owner', 'admin', 'manager'].includes(authUser.role || '')) return;
    const token = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d)) return;
        setOutlets(d);
        const navMatch = outletId != null && d.some((o: any) => o.id === outletId);
        if (navMatch) setSelectedId(outletId);
        else if (d.length > 0) setSelectedId(d[0].id);
      })
      .catch(() => {});
  }, [authUser]);

  // Keep this section's selected branch in sync whenever the navbar outlet changes
  useEffect(() => {
    if (outletId == null) return;
    if (outlets.some(o => o.id === outletId)) setSelectedId(outletId);
  }, [outletId, outlets]);

  if (!authUser || !['owner', 'admin', 'manager'].includes(authUser.role || '') || outlets.length === 0) return null;

  const selectedOutlet = outlets.find(o => o.id === selectedId);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
        <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-white border border-slate-700 text-base">🖥️</div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-800">Per-Outlet Attendance Devices</h2>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">ZKTeco / USB fingerprint agent — one key per branch</p>
        </div>
      </div>
      <OutletContextPill outlet={selectedOutlet ?? null} />
      {outlets.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
          {outlets.map(o => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                selectedId === o.id ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              🏪 {o.name}
            </button>
          ))}
        </div>
      )}
      {selectedOutlet && <OutletAttendanceDeviceCard outlet={selectedOutlet} />}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[10px] text-blue-700 font-medium">
        💡 Generate a key for this outlet, then paste it into the CloudAIRambo Agent app running on that branch's PC connected to its ZKTeco device.
      </div>
    </div>
  );
}
function MonthlyUsageBadge({ authUser, moduleStatuses = {} }: { authUser: any; moduleStatuses?: Record<string, string> }) {
  const [usage, setUsage] = React.useState<{ count: number; limit: number | null; reset_date: string } | null>(null);

  React.useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
   
    fetch(`${BACKEND}/invoices/monthly-usage`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json()).then(setUsage).catch(() => {});
  }, []);

  const p = (authUser as any)?.plan || 'basic';
  const pl = p === 'ultra' ? '⚡ Ultra' : p === 'pro' ? '✨ Pro' : '🔓 Basic · Free Forever';
  const pc = p === 'ultra' ? 'bg-amber-50 text-amber-700 border-amber-200' : p === 'pro' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';

  // Don't show Basic badge if a module pill will appear below
  const hasActiveModule = p === 'basic' && Object.values(moduleStatuses).some((s: any) => s === 'active' || s === 'trial');
  const isTrialOrg = usage?.limit === null;
  const isAnyModuleTrial = Object.values(moduleStatuses).some((s: any) => s === 'trial');
  const showUnlimited = isTrialOrg || isAnyModuleTrial;

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {!hasActiveModule && <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${pc}`}>{pl}</div>}
      {usage?.limit && (
        <div className="w-full mt-2">
          <div className="flex justify-between text-[9px] font-bold text-slate-500 mb-1">
            <span>📄 Monthly Invoices</span>
            <span style={{ color: showUnlimited ? '#16a34a' : usage.count >= usage.limit ? '#dc2626' : '#16a34a' }}>
              {showUnlimited ? '∞ Unlimited' : `${usage.count} / ${usage.limit}`}
            </span>
          </div>
          <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 99, width: `${Math.min(100, (usage.count / usage.limit) * 100)}%`, background: usage.count >= usage.limit ? '#dc2626' : usage.count >= usage.limit * 0.8 ? '#f59e0b' : '#16a34a', transition: 'width 0.3s' }} />
          </div>
          <div className="text-[9.5px] text-slate-400 mt-1 flex justify-between items-center">
            <span>Free Quota Reset:</span>
            <span className="font-semibold text-slate-600">{usage.reset_date}</span>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── Component ────────────────────────────────────────────────────────────────

// Replaces any hardcoded product name in message templates with the {company}
// placeholder, so each customer's own company name is used when sent.
function brandTemplates(t: MessageTemplates): MessageTemplates {
  const out: any = {};
  Object.entries(t).forEach(([k, v]) => {
    out[k] = typeof v === 'string' ? v.replace(/CloudAIRambo/gi, '{company}') : v;
  });
  return out as MessageTemplates;
}
function DeleteAccountSection({ authUser, onLogout }: { authUser: AuthUser; onLogout: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const handleDelete = async () => {
    setError('');
    if (!password && confirmText.trim().toUpperCase() !== 'DELETE') {
      setError("Please enter your account password OR type 'DELETE' to confirm.");
      return;
    }

    if (!confirm('Are you ABSOLUTELY sure? This will permanently delete your account and cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const res = await fetch(`${BACKEND}/auth/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          password: password || null,
          confirmation: confirmText.trim().toUpperCase() || null,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Delete failed — please check your password');
        return;
      }

      localStorage.removeItem('cloudairambo_jwt');
      localStorage.removeItem('cloudairambo_company_profile');
      alert('Your account has been deleted.');
      onLogout();
    } catch (e: any) {
      setError(e.message || 'Network error — could not reach server');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-red-600">Delete Account</h1>
        <p className="text-xs text-slate-500 mt-0.5">Permanently remove your account and all associated access</p>
      </div>

      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-2.5 border-b border-red-100 pb-4">
          <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-600 border border-red-100 text-base">
            ⚠️
          </div>
          <div>
            <h2 className="text-sm font-extrabold text-slate-900">Danger Zone — Deletion Warning</h2>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">Logged in as {authUser.email}</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 space-y-1.5 text-red-700 text-[11px] leading-relaxed">
          <p className="font-bold">What happens when you delete your account:</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Your active subscription and trials will be cancelled immediately.</li>
            <li>All active sessions on all devices will be logged out.</li>
            <li>If you are an organization owner, organization access for all team members will be revoked.</li>
            <li>This action cannot be undone.</li>
          </ul>
        </div>

        <div className="space-y-3 pt-2">
          <div className="space-y-1">
            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              Account Password (or leave blank if you used Google)
            </label>
            <input
              type="password"
              placeholder="Your current password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              Type <span className="font-bold text-red-600">DELETE</span> to confirm
            </label>
            <input
              type="text"
              placeholder="DELETE"
              value={confirmText}
              onChange={e => { setConfirmText(e.target.value); setError(''); }}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-[10px] text-red-700 font-bold">
              ❌ {error}
            </div>
          )}

          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-xs transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {deleting ? 'Deleting account...' : 'Permanently Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
}
export default function ProfileView({
  authUser,
  googleUser,
  googleToken,
  activeCurrency,
  outletId,
  storageOption,
  onCurrencyChange,
  onStorageChange,
  onLogin,
  onLogout,
  onGoogleLogin,
  onGoogleLogout,
  onShowAuth,
  onUpgrade,
  onShowPlans,
  moduleStatuses = {},
  moduleTrialEndDates = {},
  modulePeriodEndDates = {},
}: ProfileViewProps) {
  const currentStorage = storageOption || 'local';
  // Currency, WhatsApp/Gmail, and Payment Gateways are fraud-sensitive — they
  // default to owner-only but can be individually granted to an admin/manager
  // by the owner via IAM (canManageCurrency / canManageProfileMessaging /
  // canManageGateways).
  const perms = getPermissions(authUser?.role, (authUser as any)?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  const [currencyDisplayMode, setCurrencyDisplayMode] = useState<'symbol' | 'code'>(
  (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
);
const [numberConfig, setNumberConfig] = useState<{ prefix: string } | null>(null);
const [allPrefixes, setAllPrefixes] = useState<{ prefix: string; counter: number }[]>([]);
const [msgTemplates, setMsgTemplates] = useState<MessageTemplates>({ ...DEFAULT_TEMPLATES });
const [msgTemplatesDirty, setMsgTemplatesDirty] = useState(false);
const [msgTemplatesSaved, setMsgTemplatesSaved] = useState(false);
const [msgSaving, setMsgSaving] = useState(false);
const [templateSubTab, setTemplateSubTab] = useState<'email' | 'whatsapp' | 'fitness' | 'membership'>('email');
const [templateOutlets, setTemplateOutlets] = useState<{ id: number; name: string }[]>([]);
const [selectedTemplateOutlet, setSelectedTemplateOutlet] = useState<number | null>(null);

  // ── Company profile state ──────────────────────────────────────────────────
  const [company, setCompany] = useState<CompanyProfile>(EMPTY_COMPANY_PROFILE);
  const [upgradeCompanyName, setUpgradeCompanyName] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [companySaved, setCompanySaved] = useState(false);
  const [companyDirty, setCompanyDirty] = useState(false);
  const claimingRef = useRef(false);

useEffect(() => {
  const token = localStorage.getItem('cloudairambo_jwt');
  if (!token || !authUser) return;
 
  // Load company profile from backend
  fetch(`${BACKEND}/auth/company-profile`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.json())
    .then(data => {
setCompany({
name: data.name || '',
domain: data.domain || '',
contacts: data.contacts || [],
senderAccount: data.sender_account || 'google',
notifyEmail: data.notify_email ?? true,
notifyWhatsApp: data.notify_whatsapp ?? false,
emailMessage: data.email_message || '',
upiId: data.upi_id || '',
taxId: data.tax_id || '',
      });
      const savedMode = (data.currency_display_mode === 'code' ? 'code' : 'symbol');
      setCurrencyDisplayMode(savedMode);
      localStorage.setItem('cloudairambo_currency_display_mode', savedMode);
    })
    .catch(() => {
      const local = loadCompanyProfile();
      setCompany(local);
    });
  // Load number config
  fetch(`${BACKEND}/invoices/number-config`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => r.json())
    .then(data => setNumberConfig(data))
    .catch(() => {});
  // Load all assigned prefixes — org-wide + every outlet — same source RN uses
  fetch(`${BACKEND}/invoices/all-prefixes`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => r.json())
    .then(d => setAllPrefixes(Array.isArray(d) ? d : []))
    .catch(() => {});
fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.json())
    .then(d => {
      if (!Array.isArray(d)) return;
      setTemplateOutlets(d);
      if (d.length > 0) setSelectedTemplateOutlet(d[0].id);
    })
    .catch(() => {});

  }, [authUser]);

  useEffect(() => {
    loadMessageTemplates(selectedTemplateOutlet).then(t => setMsgTemplates(brandTemplates(t)));
    setMsgTemplatesDirty(false);
    setMsgTemplatesSaved(false);
  }, [selectedTemplateOutlet]);

  const updateCompany = (patch: Partial<CompanyProfile>) => {
    setCompany((prev) => ({ ...prev, ...patch }));
    setCompanyDirty(true);
    setCompanySaved(false);
  };

  const handleSaveCompany = async () => {
    // Save to backend
    const token = localStorage.getItem('cloudairambo_jwt');
  
    if (token && authUser?.org_id) {
      try {
        await fetch(`${BACKEND}/auth/company-profile`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            name: company.name,
            domain: company.domain,
            contacts: company.contacts,
            currency: activeCurrency,
            notify_email: company.notifyEmail,
            notify_whatsapp: company.notifyWhatsApp,
            email_message: company.emailMessage,
            sender_account: company.senderAccount,
            upi_id: company.upiId,
            tax_id: company.taxId,
          }),
        });
      } catch (e) { console.warn('Backend save failed:', e); }
    }
    saveCompanyProfile(company); // keep localStorage as backup
    setCompanyDirty(false);
    setCompanySaved(true);
    setTimeout(() => setCompanySaved(false), 2500);

    // Auto-claim unique prefix from company name
    if (company.name?.trim()) {
      const raw = company.name.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      const desired = raw.slice(0, 3) || 'INV';
      if (claimingRef.current) return;
      claimingRef.current = true;
      try {
        const claimToken = localStorage.getItem('cloudairambo_jwt');
        await fetch(`${BACKEND}/invoices/claim-prefix`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(claimToken ? { Authorization: `Bearer ${claimToken}` } : {}),
          },
          
          body: JSON.stringify({ prefix: desired }),
        });
      } catch (e) {
        console.warn('Prefix claim failed:', e);
      } finally {
        claimingRef.current = false;
      }
      // Re-fetch updated prefix
      const refreshToken = localStorage.getItem('cloudairambo_jwt');
      if (refreshToken) {
        fetch(`${BACKEND}/invoices/number-config`, {
          headers: { Authorization: `Bearer ${refreshToken}` },
        })
          .then(r => r.json())
          .then(data => setNumberConfig(data))
          .catch(() => {});
      }
    }
  };
const handleCurrencyDisplayModeChange = (mode: 'symbol' | 'code') => {
  setCurrencyDisplayMode(mode);
  localStorage.setItem('cloudairambo_currency_display_mode', mode);
  const token = localStorage.getItem('cloudairambo_jwt');
  if (token) {
    fetch(`${BACKEND}/auth/company-profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ currency_display_mode: mode }),
    }).catch(() => {});
  }
};
  // ── Contact entries helpers ────────────────────────────────────────────────
  const addContact = () => {
    const newEntry: ContactEntry = {
      id: `contact_${Date.now()}`,
      type: 'phone',
      label: '',
      value: '',
      showInFooter: true,
    };
    updateCompany({ contacts: [...company.contacts, newEntry] });
  };

  const updateContact = (id: string, patch: Partial<ContactEntry>) => {
    updateCompany({
      contacts: company.contacts.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      ),
    });
  };

  const removeContact = (id: string) => {
    updateCompany({ contacts: company.contacts.filter((c) => c.id !== id) });
  };

  // Google email for display
  const googleEmail =
    googleUser?.email || authUser?.email || null;

  // Derived: company email entry (first one)
  const companyEmailEntry = company.contacts.find((c) => c.type === 'email');

  type TabType =
    | 'profile'
    | 'company'
    | 'currency'
    | 'orgid'
    | 'templates'
    | 'whatsapp'
    | 'email'
    | 'gateways'
    | 'counters'
    | 'devices'
    | 'badge'
    | 'subscription'
    | 'referrals'
    | 'delete';

  const VALID_TABS: TabType[] = [
    'profile', 'company', 'currency', 'orgid', 'templates',
    'whatsapp', 'email', 'gateways', 'counters', 'devices',
    'badge', 'subscription', 'referrals', 'delete',
  ];

  const getInitialTab = (): TabType => {
    const hash = window.location.hash.replace('#', '') as TabType;
    if (VALID_TABS.includes(hash)) return hash;

    const queryTab = new URLSearchParams(window.location.search).get('tab') as TabType;
    if (VALID_TABS.includes(queryTab)) return queryTab;

    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (path === 'delete' || path === 'delete-account') return 'delete';

    return 'profile';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);

  // Changes tab AND updates the browser address bar in real time
  const selectTab = (tab: TabType) => {
    setActiveTab(tab);
    window.history.pushState(null, '', `#${tab}`);
  };

  // Listen to browser Back/Forward buttons and direct URL changes
  useEffect(() => {
    const syncFromUrl = () => {
      const target = getInitialTab();
      setActiveTab(target);
    };
    window.addEventListener('hashchange', syncFromUrl);
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('hashchange', syncFromUrl);
      window.removeEventListener('popstate', syncFromUrl);
    };
  }, []);

  const NavItem = ({ id, icon, label, badge }: { id: typeof activeTab; icon: React.ReactNode; label: string; badge?: number }) => (
    <button
      onClick={() => { selectTab(id); setDrawerOpen(false); }}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px', borderRadius: 8, textAlign: 'left',
        transition: 'all 0.15s', fontSize: 14, fontWeight: 500,
        background: activeTab === id ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)' : 'transparent',
        color: activeTab === id ? '#1d4ed8' : '#64748b',
        boxShadow: activeTab === id ? 'inset 0 1px 2px rgba(0,0,0,0.05)' : 'none',
      }}
      onMouseEnter={e => { if (activeTab !== id) { (e.currentTarget as HTMLElement).style.background = '#eff6ff'; (e.currentTarget as HTMLElement).style.color = '#1d4ed8'; } }}
      onMouseLeave={e => { if (activeTab !== id) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#64748b'; } }}
    >
      <span className="w-5 h-5 flex items-center justify-center shrink-0">{icon}</span>
      <span className="flex-1">{label}</span>
      {badge ? <span className="bg-violet-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span> : null}
    </button>
  );

  const plan = (authUser as any)?.plan || 'basic';
  const planLabel = plan === 'ultra' ? '⚡ Ultra' : plan === 'pro' ? '✨ Pro' : '🔓 Basic (Free)';

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [showProfileModulesDropdown, setShowProfileModulesDropdown] = useState(false);
  const [showSidebarModulesDropdown, setShowSidebarModulesDropdown] = useState(false);
  const [sidebarModulesDropdownPos, setSidebarModulesDropdownPos] = useState({ top: 0, left: 0 });
  const sidebarModulesBtnRef = useRef<HTMLButtonElement>(null);

  return (
  <>
    <style>{`
      .profile-scroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      .profile-scroll::-webkit-scrollbar {
        width: 0px;
        height: 0px;
        display: none;
      }
    `}</style>
    <div 
      className="flex h-full overflow-hidden select-none relative"
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >

      {/* ── Mobile backdrop ── */}
      {isMobile && drawerOpen && (
        <div onClick={() => setDrawerOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <div className="w-80 shrink-0 flex flex-col h-full overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #f5f9ff, #eaf2fc)',
          borderRight: '1px solid #dbeafe',
          ...(isMobile ? {
            position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 50,
            transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.25s ease',
          } : {}),
        }}>

        {/* User block */}
        <div className="p-4" style={{ borderBottom: '1px solid #dbeafe' }}>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
              {googleUser?.photoURL
                ? <img src={googleUser.photoURL} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full" />
                : <span>{(authUser?.name || googleUser?.displayName || 'G')?.charAt(0).toUpperCase()}</span>
              }
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold truncate" style={{ color: '#1e3a5f' }}>{authUser?.name || googleUser?.displayName || 'Guest'}</p>
              <p className="text-[9px] truncate" style={{ color: '#7c93b3' }}>{authUser?.role || 'viewer'}</p>
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-1.5">
            <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold" style={{ background: '#ede9fe', color: '#6d28d9' }}>
              {planLabel}
            </span>
            {(() => {
              const now = Date.now();
              const parseUtc = (iso: string) => new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
              const fmtLeft = (iso: string) => {
                const diff = parseUtc(iso) - now;
                if (diff <= 0) return '0s';
                const s = Math.floor(diff / 1000);
                const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
                const m = Math.floor((s % 3600) / 60);
                if (d > 0) return `${d}d ${h}h`;
                if (h > 0) return `${h}h ${m}m`;
                return `${m}m ${s % 60}s`;
              };
              const labels: Record<string, string> = {
                ledger: 'Ledger', employees: 'Staff',
                queue: 'Queue', gym: 'Gym', canteen: 'Canteen', pos: 'POS',
              };
              const moduleRows = (['ledger', 'employees', 'queue', 'gym', 'canteen', 'pos'] as string[])
                .map(id => {
                  const st = moduleStatuses[id];
                  if (!st || (st !== 'active' && st !== 'trial' && st !== 'locked')) return null;
                  let timeText = '';
                  if (st === 'trial') {
                    const end = moduleTrialEndDates[id];
                    timeText = end ? `Trial · ${fmtLeft(end)} left` : 'Trial';
                  } else if (st === 'active') {
                    const end = modulePeriodEndDates[id];
                    timeText = end ? `Renews in ${fmtLeft(end)}` : 'Active';
                  } else if (st === 'locked') {
                    timeText = 'Locked';
                  }
                  return { id, label: labels[id] || id, status: st, timeText };
                })
                .filter(Boolean) as { id: string; label: string; status: string; timeText: string }[];

              const activeModuleCount = moduleRows.length;
              if (activeModuleCount === 0) return null;

              return (
                <div style={{ position: 'relative' }}>
                  <button
                    ref={sidebarModulesBtnRef}
                    onClick={() => {
                      const rect = sidebarModulesBtnRef.current?.getBoundingClientRect();
                      if (rect) setSidebarModulesDropdownPos({ top: rect.bottom + 6, left: rect.left });
                      setShowSidebarModulesDropdown(v => !v);
                    }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                  
                      fontSize: 9, fontWeight: 800,
                      padding: '2px 8px', borderRadius: 99,
                      background: '#f0fdf4', color: '#16a34a',
                      border: '1px solid #bbf7d0', cursor: 'pointer',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
  {activeModuleCount} module{activeModuleCount === 1 ? '' : 's'}
  <ChevronDown className="w-3 h-3" />
</span>
                  </button>
                  {showSidebarModulesDropdown && (
                    <>
                      <div onClick={() => setShowSidebarModulesDropdown(false)}
                        style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                      <div style={{
                        position: 'fixed',
                        top: sidebarModulesDropdownPos.top,
                        left: Math.min(sidebarModulesDropdownPos.left, window.innerWidth - 280),
                        background: '#fff', borderRadius: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                        border: '1px solid #e2e8f0', padding: 14, width: 264, zIndex: 50,
                      }}>
                        <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Active Modules</p>
                        {moduleRows.map((m, i) => (
                          <div key={m.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                          }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{m.label}</span>
                            <span style={{
                              fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
                              background: m.status === 'locked' ? '#fef2f2' : '#f0fdf4',
                              color: m.status === 'locked' ? '#dc2626' : '#16a34a',
                              border: `1px solid ${m.status === 'locked' ? '#fecaca' : '#bbf7d0'}`,
                            }}>
                              {m.status === 'locked' ? '🔒' : '✓'} {m.timeText}
                            </span>
                          </div>
                        ))}
                        <button onClick={() => setShowSidebarModulesDropdown(false)}
                          style={{ width: '100%', marginTop: 10, padding: '8px 0', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            {onShowPlans && (
              <button onClick={onShowPlans} className="text-[9px] text-white/30 hover:text-white/60 transition-colors">Upgrade →</button>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5 profile-scroll">
          <p className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-widest" style={{ color: '#93b4dc' }}>Account</p>
          <NavItem id="profile" label="Profile" icon={<User className="w-3.5 h-3.5" />} />
          {authUser?.role !== 'individual' && <NavItem id="company" label="Company" icon={<Building2 className="w-3.5 h-3.5" />} />}
          {perms.canManageCurrency && <NavItem id="currency" label="Currency & Region" icon={<Globe className="w-3.5 h-3.5" />} />}
           <NavItem id="orgid" label="Organization ID" icon={<span className="text-xs">🏢</span>} />
          {/*{authUser && <NavItem id="referrals" label="Refer & Earn" icon={<span className="text-xs">🎁</span>} />}*/}
          <p className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-widest" style={{ color: '#93b4dc' }}>Messaging</p>
          <NavItem id="templates" label="Templates" icon={<Mail className="w-3.5 h-3.5" />} />
          {perms.canManageProfileMessaging && <NavItem id="whatsapp" label="WhatsApp" icon={
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          } />}
          {perms.canManageProfileMessaging && <NavItem id="email" label="Email" icon={<Mail className="w-3.5 h-3.5" />} />}

          <p className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-widest" style={{ color: '#93b4dc' }}>Payments</p>
          {perms.canManageGateways && <NavItem id="gateways" label="Gateways" icon={<Shield className="w-3.5 h-3.5" />} />}

          <p className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-widest" style={{ color: '#93b4dc' }}>System</p>
          <NavItem id="counters" label="Serial Counters" icon={<Lock className="w-3.5 h-3.5" />} />
          {authUser && ['owner', 'admin', 'manager'].includes(authUser.role || '') && (
            <NavItem id="devices" label="Devices" icon={<Settings className="w-3.5 h-3.5" />} />
          )}
          {authUser && <NavItem id="badge" label="Cashier Badge" icon={<Shield className="w-3.5 h-3.5" />} />}
          {authUser && <NavItem id="subscription" label="Subscription" icon={<span>💎</span>} />}
          {authUser && (
            <NavItem
              id="delete"
              label="Delete Account"
              icon={<Trash2 className="w-3.5 h-3.5 text-red-500" />}
            />
          )}
        </nav>

        {/* Sign out */}
        <div className="p-2" style={{ borderTop: '1px solid #dbeafe' }}>
          {authUser && (
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[11px] transition-all"
              style={{ color: '#7c93b3' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#dc2626'; (e.currentTarget as HTMLElement).style.background = '#fdeaea'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#7c93b3'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <LogOut className="w-3.5 h-3.5 shrink-0" />Sign out
            </button>
          )}
          {!authUser && !googleUser && (
            <button
              onClick={onShowAuth}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[11px] transition-all"
              style={{ color: '#64748b' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#1d4ed8'; (e.currentTarget as HTMLElement).style.background = '#eff6ff'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#64748b'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <User className="w-3.5 h-3.5 shrink-0" />Sign in
            </button>
          )}
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="flex-1 overflow-y-auto bg-slate-50 profile-scroll">
        {isMobile && (
          <div style={{ position: 'sticky', top: 0, zIndex: 30, background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setDrawerOpen(true)}
              style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}>
              ☰
            </button>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Profile Settings</span>
          </div>
        )}
        <div className="max-w-3xl mx-auto p-6 space-y-4">

          {/* ── PROFILE TAB ── */}
          {activeTab === 'profile' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Profile</h1><p className="text-xs text-slate-500 mt-0.5">Your account details and preferences</p></div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0 shadow-inner">
                  {googleUser?.photoURL ? <img src={googleUser.photoURL} alt="avatar" referrerPolicy="no-referrer" className="w-14 h-14 rounded-full border-2 border-white shadow" /> : authUser ? <span className="text-2xl font-extrabold text-blue-600">{authUser.name?.charAt(0).toUpperCase()}</span> : <User className="w-7 h-7" />}
                </div>
                <div className="min-w-0">
                  <div className="font-extrabold text-slate-900 truncate">{authUser?.name || googleUser?.displayName || 'Guest User'}</div>
                  <div className="text-xs text-slate-500 font-mono truncate mt-0.5">{authUser?.email || googleUser?.email || 'No account linked'}</div>
    
                  <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${authUser || googleUser ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                    {authUser ? <><Shield className="w-3 h-3" /> CloudAIRambo Account</> : googleUser ? <><CheckCircle className="w-3 h-3" /> Google Account</> : <><User className="w-3 h-3" /> Guest Mode</>}
                  </div>
                {authUser && (() => {
                  const now = Date.now();
                  const parseUtc = (iso: string) => new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
                  const fmtLeft = (iso: string) => {
                    const diff = parseUtc(iso) - now;
                    if (diff <= 0) return '0s';
                    const s = Math.floor(diff / 1000);
                    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
                    const m = Math.floor((s % 3600) / 60);
                    if (d > 0) return `${d}d ${h}h`;
                    if (h > 0) return `${h}h ${m}m`;
                    return `${m}m ${s % 60}s`;
                  };
                  const labels: Record<string, string> = {
                    ledger: 'Ledger', employees: 'Staff',
                    queue: 'Queue', gym: 'Gym', canteen: 'Canteen', pos: 'POS',
                  };
                  const moduleRows = (['ledger', 'employees', 'queue', 'gym', 'canteen', 'pos'] as string[])
                    .map(id => {
                      const st = moduleStatuses[id];
                      if (!st || (st !== 'active' && st !== 'trial' && st !== 'locked')) return null;
                      let timeText = '';
                      if (st === 'trial') {
                        const end = moduleTrialEndDates[id];
                        timeText = end ? `Trial · ${fmtLeft(end)} left` : 'Trial';
                      } else if (st === 'active') {
                        const end = modulePeriodEndDates[id];
                        timeText = end ? `Renews in ${fmtLeft(end)}` : 'Active';
                      } else if (st === 'locked') {
                        timeText = 'Locked';
                      }
                      return { id, label: labels[id] || id, status: st, timeText };
                    })
                    .filter(Boolean) as { id: string; label: string; status: string; timeText: string }[];

                  const activeModuleCount = moduleRows.length;

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <MonthlyUsageBadge authUser={authUser} moduleStatuses={moduleStatuses} />
                      {activeModuleCount > 0 && (
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => setShowProfileModulesDropdown(v => !v)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              
                              fontSize: 10, fontWeight: 800,
                              padding: '2px 8px', borderRadius: 99,
                              background: '#f0fdf4', color: '#16a34a',
                              border: '1px solid #bbf7d0', cursor: 'pointer',
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
  {activeModuleCount} module{activeModuleCount === 1 ? '' : 's'}
  <ChevronDown className="w-3 h-3" />
</span>
                          </button>
                          {showProfileModulesDropdown && (
                            <>
                              <div onClick={() => setShowProfileModulesDropdown(false)}
                                style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                              <div style={{
                                position: 'absolute', top: '100%', left: 0, marginTop: 6,
                                background: '#fff', borderRadius: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                                border: '1px solid #e2e8f0', padding: 14, minWidth: 260, zIndex: 50,
                              }}>
                                <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Active Modules</p>
                                {moduleRows.map((m, i) => (
                                  <div key={m.id} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                                  }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{m.label}</span>
                                    <span style={{
                                      fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
                                      background: m.status === 'locked' ? '#fef2f2' : '#f0fdf4',
                                      color: m.status === 'locked' ? '#dc2626' : '#16a34a',
                                      border: `1px solid ${m.status === 'locked' ? '#fecaca' : '#bbf7d0'}`,
                                    }}>
                                      {m.status === 'locked' ? '🔒' : '✓'} {m.timeText}
                                    </span>
                                  </div>
                                ))}
                                <button onClick={() => setShowProfileModulesDropdown(false)}
                                  style={{ width: '100%', marginTop: 10, padding: '8px 0', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
                                  Close
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                </div>
              </div>
              {!authUser && !googleUser && (
                <div className="space-y-2.5">
                  <p className="text-xs text-slate-500 leading-relaxed font-medium">You're browsing as a guest. Create an account or sign in to sync your data across devices.</p>
                  <button onClick={onShowAuth} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all">Create Account / Sign In</button>
                  <button onClick={onGoogleLogin} className="w-full py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2.5">
                    <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                    Continue with Google
                  </button>
                </div>
              )}
              {(authUser || googleUser) && (
                <div className="space-y-2">
                  {authUser && <button onClick={onLogout} className="w-full py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2"><LogOut className="w-3.5 h-3.5" />Sign Out of CloudAIRambo</button>}
                  {googleUser && <button onClick={onGoogleLogout} className="w-full py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2"><LogOut className="w-3.5 h-3.5" />Disconnect Google</button>}
                  {authUser && (
  <div className="pt-2 border-t border-slate-100">
    <p className="text-[9px] font-black uppercase tracking-wider text-slate-500 mb-2">Organization-wide Default Gmail</p>
    <p className="text-[9px] text-slate-400 mb-2">Used automatically for any branch that hasn't connected its own Gmail.</p>
    <OrgGmailCard orgEmail={(authUser as any)?.gmail_connected_email || null} />
  </div>
)}
                </div>
              )}
            </div>
            {authUser && perms.canManageProfileMessaging && (
              <OrgWideWhatsAppCard />
            )}
            {authUser && authUser.role === 'individual' && (
              <div className="bg-white rounded-2xl border border-purple-200 shadow-sm p-5 space-y-3">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 border border-purple-100"><Building2 className="w-4 h-4" /></div>
                  <div><h2 className="text-sm font-extrabold text-slate-800">Upgrade to Business</h2><p className="text-[10px] text-slate-400 font-medium mt-0.5">One-way upgrade — cannot be undone</p></div>
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 space-y-1">
                  {['Add team members (staff, admin, viewer)', 'Role-based permissions', 'Shared invoice workspace'].map(f => (
                    <p key={f} className="text-[10px] text-purple-700 font-medium flex items-center gap-1.5"><CheckCircle className="w-3 h-3 shrink-0" /> {f}</p>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5"><p className="text-[10px] text-amber-700 font-bold">⚠ This is permanent. Once upgraded you cannot revert to an individual account.</p></div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Company Name</label>
                  <input type="text" placeholder="Your Company Pvt Ltd" value={upgradeCompanyName} onChange={e => { setUpgradeCompanyName(e.target.value); setUpgradeError(''); }} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition-all" />
                </div>
                {upgradeError && <p className="text-[10px] text-red-600 font-semibold">{upgradeError}</p>}
                <button disabled={upgrading} onClick={async () => {
                  if (!upgradeCompanyName.trim()) { setUpgradeError('Enter your company name first'); return; }
                  if (!confirm('Upgrade to Business? This cannot be undone.')) return;
                  try { setUpgrading(true); setUpgradeError(''); const u = await upgradeToOrganization(upgradeCompanyName.trim()); onUpgrade(u); }
                  catch (e: any) { setUpgradeError(e.message); } finally { setUpgrading(false); }
                }} className="w-full py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-sm disabled:opacity-60">
                  {upgrading ? 'Upgrading...' : '🏢 Upgrade to Business Account'}
                </button>
              </div>
            )}
          </>)}

          {activeTab === 'orgid' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Organization ID</h1><p className="text-xs text-slate-500 mt-0.5">Your API key for payment QR codes, payment links, and integrations</p></div>
            <OrgApiKeyCard />
          </>)}

          {/* ── COMPANY TAB ── */}
          {activeTab === 'company' && authUser?.role !== 'individual' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Company</h1><p className="text-xs text-slate-500 mt-0.5">Shown on invoices, email footers & shared messages</p></div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100"><Building2 className="w-4 h-4" /></div>
                  <div><h2 className="text-sm font-extrabold text-slate-800">Company Details</h2><p className="text-[10px] text-slate-400 font-medium mt-0.5">Name, domain, UPI, contacts & notifications</p></div>
                </div>
                {companyDirty && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Unsaved changes</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company Name</label>
                  <input type="text" placeholder="Your Company Pvt Ltd" value={company.name} onChange={e => updateCompany({ name: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1"><Globe className="w-3 h-3" /> Domain / Website <span className="text-slate-300 font-normal normal-case ml-1">optional</span></label>
                  <input type="url" placeholder="https://yourcompany.com" value={company.domain} onChange={e => updateCompany({ domain: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all font-mono" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">💳 UPI ID <span className="text-slate-300 font-normal normal-case ml-1">for India POS payments</span></label>
                <input type="text" placeholder="yourname@okaxis / yourname@upi" value={company.upiId} onChange={e => updateCompany({ upiId: e.target.value })} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all" />
                <p className="text-[9px] text-slate-400">When set, the POS display QR will open GPay / PhonePe / Paytm directly.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">🧾 Tax ID <span className="text-slate-300 font-normal normal-case ml-1">org default — e.g. GSTIN, VAT No.</span></label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Your company's tax registration number"
                    maxLength={25}
                    value={company.taxId}
                    onChange={e => updateCompany({ taxId: e.target.value.replace(/[\s\-\.]/g, '').toUpperCase() })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all"
                    style={{ paddingRight: 80, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  />
                  {company.taxId.length > 0 && (() => {
                    const meta = inspectTaxId(company.taxId, '');
                    return (
                      <div style={{ position: 'absolute', right: 10, pointerEvents: 'none' }}>
                        {meta.isComplete || meta.currentLength >= meta.expectedLength ? (
                          <span className="text-[11px] font-black" style={{ color: meta.isValid ? '#16a34a' : '#ef4444' }}>
                            {meta.isValid ? '✓ Valid' : '✗ Invalid'}
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-400">{meta.currentLength}/{meta.expectedLength}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {company.taxId.length >= 2 && (() => {
                  const meta = inspectTaxId(company.taxId, '');
                  return (
                    <div
                      className="flex items-center rounded-xl px-2.5 py-1.5 gap-1.5 text-[11px]"
                      style={{
                        backgroundColor: (meta.isComplete && !meta.isValid) ? '#fef2f2' : meta.isValid ? '#f0fdf4' : '#eff6ff',
                        border: `1px solid ${(meta.isComplete && !meta.isValid) ? '#fecaca' : meta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{meta.countryFlag}</span>
                      <span className="font-bold text-slate-700 flex-1">
                        {meta.countryName}
                        {meta.detectedRegion ? ` · ${meta.detectedRegion}` : ''}
                        <span className="text-slate-400 font-medium"> ({meta.taxLabel})</span>
                      </span>
                      {meta.detectedRegion && (
                        <span className="text-[9px] font-black text-emerald-600">✓ State detected</span>
                      )}
                    </div>
                  );
                })()}
                <p className="text-[9px] text-slate-400">
                  Used as the seller's tax ID on invoices. If your outlets are registered in different states, set an override per outlet under Currency &amp; Region.
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Contact Entries <span className="ml-2 text-slate-300 font-normal normal-case">all optional</span></p>
                  <span className="text-[9px] font-bold font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{company.contacts.length} added</span>
                </div>
                <div className="space-y-2">
                  {company.contacts.map(entry => {
                    const meta = CONTACT_TYPE_META[entry.type];
                    return (
                      <div key={entry.id} className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                        <div className="relative shrink-0">
                          <select value={entry.type} onChange={e => updateContact(entry.id, { type: e.target.value as ContactEntryType, label: '' })} className="appearance-none pl-7 pr-6 py-1.5 bg-white border border-slate-200 rounded-lg text-[10.5px] font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer">
                            {(Object.keys(CONTACT_TYPE_META) as ContactEntryType[]).map(t => <option key={t} value={t}>{CONTACT_TYPE_META[t].label}</option>)}
                          </select>
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">{meta.icon}</span>
                          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                        </div>
                        {entry.type === 'custom' && <input type="text" placeholder="Label name" value={entry.label} onChange={e => updateContact(entry.id, { label: e.target.value })} className="w-24 shrink-0 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-[10.5px] font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500" />}
                        <input type={entry.type === 'email' ? 'email' : 'text'} placeholder={meta.placeholder} value={entry.value} onChange={e => updateContact(entry.id, { value: e.target.value })} className="flex-1 min-w-0 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-[10.5px] font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        <label className="flex items-center gap-1 shrink-0 cursor-pointer">
                          <input type="checkbox" checked={entry.showInFooter} onChange={e => updateContact(entry.id, { showInFooter: e.target.checked })} className="w-3.5 h-3.5 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer" />
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Footer</span>
                        </label>
                        <button onClick={() => removeContact(entry.id)} className="shrink-0 p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={addContact} className="w-full flex items-center justify-center gap-2 py-2 border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/30 rounded-xl text-[10.5px] font-bold text-slate-500 hover:text-blue-600 transition-all cursor-pointer"><Plus className="w-3.5 h-3.5" />Add another contact entry</button>
              </div>
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <div><p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Sender Account</p><p className="text-[10px] text-slate-400 mt-0.5">When sending email reminders, send from which account?</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button onClick={() => updateCompany({ senderAccount: 'google' })} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${company.senderAccount === 'google' ? 'border-blue-500 bg-blue-50/40' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${company.senderAccount === 'google' ? 'bg-white shadow-sm' : 'bg-slate-100'}`}>
                      <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                    </div>
                    <div className="min-w-0"><p className="text-xs font-bold text-slate-800">Google Account</p><p className="text-[10px] text-slate-500 font-mono truncate mt-0.5">{googleEmail || 'Not connected'}</p>{!googleToken && <span className="text-[9px] text-amber-600 font-bold">⚠ Requires Google login</span>}</div>
                    <div className={`w-4 h-4 rounded-full border-2 ml-auto shrink-0 flex items-center justify-center ${company.senderAccount === 'google' ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}>{company.senderAccount === 'google' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}</div>
                  </button>
                  <button onClick={() => updateCompany({ senderAccount: 'company' })} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${company.senderAccount === 'company' ? 'border-blue-500 bg-blue-50/40' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${company.senderAccount === 'company' ? 'bg-white shadow-sm' : 'bg-slate-100'}`}><Mail className="w-4 h-4 text-blue-500" /></div>
                    <div className="min-w-0"><p className="text-xs font-bold text-slate-800">Company Email</p><p className="text-[10px] text-slate-500 font-mono truncate mt-0.5">{companyEmailEntry?.value || 'Add a company email above'}</p>{!companyEmailEntry?.value && <span className="text-[9px] text-amber-600 font-bold">⚠ No company email set</span>}</div>
                    <div className={`w-4 h-4 rounded-full border-2 ml-auto shrink-0 flex items-center justify-center ${company.senderAccount === 'company' ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}>{company.senderAccount === 'company' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}</div>
                  </button>
                </div>
                {company.contacts.filter(c => c.showInFooter && c.value).length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5">
                    <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">Email / message footer preview</p>
                    <div className="flex flex-wrap gap-2">
                      {company.contacts.filter(c => c.showInFooter && c.value).map(c => {
                        const meta = CONTACT_TYPE_META[c.type];
                        return <span key={c.id} className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded-lg"><span className="text-slate-400">{meta.icon}</span>{c.type === 'custom' && c.label ? `${c.label}: ` : ''}{c.value}</span>;
                      })}
                      {company.domain && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded-lg"><Globe className="w-3 h-3 text-slate-400" />{company.domain}</span>}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <div><p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Notification Preferences</p><p className="text-[10px] text-slate-400 mt-0.5">Auto-send to client after every invoice is saved</p></div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => updateCompany({ notifyEmail: !company.notifyEmail })} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${company.notifyEmail ? 'border-blue-500 bg-blue-50/40' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${company.notifyEmail ? 'bg-blue-100' : 'bg-slate-100'}`}><Mail className="w-4 h-4 text-blue-500" /></div>
                    <div className="min-w-0"><p className="text-xs font-bold text-slate-800">Email</p><p className="text-[9px] text-slate-500 mt-0.5">{company.notifyEmail ? '✓ Enabled' : 'Disabled'}</p></div>
                    <div className={`w-4 h-4 rounded-full border-2 ml-auto shrink-0 flex items-center justify-center ${company.notifyEmail ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}>{company.notifyEmail && <div className="w-1.5 h-1.5 rounded-full bg-white" />}</div>
                  </button>
                  <button onClick={() => updateCompany({ notifyWhatsApp: !company.notifyWhatsApp })} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer ${company.notifyWhatsApp ? 'border-green-500 bg-green-50/40' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${company.notifyWhatsApp ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </div>
                    <div className="min-w-0"><p className="text-xs font-bold text-slate-800">WhatsApp</p><p className="text-[9px] text-slate-500 mt-0.5">{company.notifyWhatsApp ? '✓ Enabled' : 'Disabled'}</p></div>
                    <div className={`w-4 h-4 rounded-full border-2 ml-auto shrink-0 flex items-center justify-center ${company.notifyWhatsApp ? 'border-green-600 bg-green-600' : 'border-slate-300 bg-white'}`}>{company.notifyWhatsApp && <div className="w-1.5 h-1.5 rounded-full bg-white" />}</div>
                  </button>
                </div>
              </div>
              {(company.notifyEmail || company.notifyWhatsApp) && (
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">Custom Message to Client</label>
                  <textarea value={company.emailMessage || ''} onChange={e => updateCompany({ emailMessage: e.target.value })} placeholder="e.g. Thank you for your business! Please pay within 7 days." rows={3} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
                </div>
              )}
              <div className="flex justify-end pt-1">
                <button onClick={handleSaveCompany} disabled={!companyDirty && !companySaved} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer ${companySaved ? 'bg-emerald-600 text-white' : companyDirty ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm' : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-default'}`}>
                  {companySaved ? <><CheckCircle className="w-4 h-4" />Saved!</> : <><Save className="w-4 h-4" />Save Company Profile</>}
                </button>
              </div>
            </div>
          </>)}

          {/* ── CURRENCY TAB ── */}
          {activeTab === 'currency' && perms.canManageCurrency && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Currency & Region</h1><p className="text-xs text-slate-500 mt-0.5">Invoice currency and default phone country</p></div>
            <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-5 space-y-3">
              <div className="border-b border-slate-100 pb-3">
                <h2 className="text-sm font-extrabold text-slate-800 flex items-center gap-1.5">🌐 Organization Defaults</h2>
                <p className="text-[10px] text-slate-400 font-medium mt-0.5">Currency, country, state and phone country used by any outlet that hasn't set its own</p>
              </div>
              <OrgDefaultsCard
                authUser={authUser}
                activeCurrency={activeCurrency}
                currencyDisplayMode={currencyDisplayMode}
                onCurrencyChange={onCurrencyChange}
                onCurrencyDisplayModeChange={handleCurrencyDisplayModeChange}
              />
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
              <div className="border-b border-slate-100 pb-3">
                <h2 className="text-sm font-extrabold text-slate-800 flex items-center gap-1.5">🏪 Per-Outlet Settings</h2>
                <p className="text-[10px] text-slate-400 font-medium mt-0.5">Currency, country, state and phone country for each branch — falls back to Organization Defaults above until set here</p>
              </div>
              {authUser && <OutletUnifiedLocationSection authUser={authUser} activeCurrency={activeCurrency} outletId={outletId} orgTaxId={company.taxId} />}
            </div>
            {authUser && allPrefixes.length > 0 && (
              <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5 space-y-3">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-100"><Lock className="w-4 h-4" /></div>
                  <div><h2 className="text-sm font-extrabold text-slate-800">Assigned Prefixes</h2><p className="text-[10px] text-slate-400 font-medium mt-0.5">Permanent · Cannot be changed</p></div>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {allPrefixes.map((p, i) => {
                    const c = PREFIX_COLORS[i % PREFIX_COLORS.length];
                    return (
                      <div
                        key={p.prefix}
                        className="rounded-xl p-3"
                        style={{ background: c.bg, border: `1px solid ${c.border}`, minWidth: '45%', flex: 1 }}
                      >
                        <p className="text-[9px] font-black uppercase tracking-wider mb-1" style={{ color: c.label }}>{p.label || 'Prefix'}</p>
                        <p className="text-xl font-black font-mono" style={{ color: c.value }}>{p.prefix}</p>
                        <p className="text-[9px] font-mono text-slate-400 mt-0.5">{`Next: ${p.prefix}-${String(p.counter).padStart(5, '0')}`}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[9px] text-slate-400 font-medium leading-relaxed">These prefixes are unique to your account and locked forever.</p>
              </div>
            )}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
              <div className="border-b border-slate-100 pb-3"><h2 className="text-sm font-extrabold text-slate-800 flex items-center gap-1.5">🧾 Per-Outlet Invoice Prefix</h2><p className="text-[10px] text-slate-400 font-medium mt-0.5">POS receipts from each outlet use their own serial number sequence based on this prefix</p></div>
              {authUser && <OutletsInvoicePrefixSection authUser={authUser} />}
            </div>

          </>)}
{activeTab === 'referrals' && (<>
  <div><h1 className="text-lg font-bold text-slate-900">Refer & Earn</h1><p className="text-xs text-slate-500 mt-0.5">Share your link, earn credit when they subscribe</p></div>
  {authUser && <ReferAndEarnCard authUser={authUser} />}
</>)}
          {/* ── TEMPLATES TAB ── */}
          {activeTab === 'templates' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Message Templates</h1><p className="text-xs text-slate-500 mt-0.5">Customize email & WhatsApp messages sent with every invoice</p></div>
            {authUser && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100"><Mail className="w-4 h-4" /></div>
                    <div><h2 className="text-sm font-extrabold text-slate-800">Message Templates</h2><p className="text-[10px] text-slate-400 font-medium mt-0.5">Customize email & WhatsApp messages sent with every invoice</p></div>
                  </div>
                  {msgTemplatesDirty && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Unsaved</span>}
                </div>
                <OutletContextPill outlet={templateOutlets.find(o => o.id === selectedTemplateOutlet) || null} />
                {templateOutlets.length > 1 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">Branch:</span>
                    {templateOutlets.map(o => (
                      <button
                        key={o.id}
                        onClick={() => setSelectedTemplateOutlet(o.id)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                          selectedTemplateOutlet === o.id ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        🏪 {o.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[
                    { id: 'email', label: 'Email', icon: <Mail className="w-3.5 h-3.5" /> },
                    { id: 'whatsapp', label: 'WhatsApp', icon: <span className="text-sm">💬</span> },
                    { id: 'fitness', label: 'Fitness & dining', icon: <span className="text-sm">💪</span> },
                    { id: 'membership', label: 'Memberships', icon: <span className="text-sm">🔔</span> },
                  ].map(t => (
                    <button key={t.id} onClick={() => setTemplateSubTab(t.id as typeof templateSubTab)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                        templateSubTab === t.id ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                      }`}>
                      {t.icon}{t.label}
                    </button>
                  ))}
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-[9px] font-black uppercase tracking-wider text-slate-400 mb-2">Available Placeholders</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['{name}', '{invoice}', '{amount}', '{currency}', '{date}', '{company}', '{phone}', '{email}'].map(p => (
                      <span key={p} className="font-mono text-[10px] bg-white border border-slate-200 text-indigo-600 px-2 py-0.5 rounded-lg font-bold">{p}</span>
                    ))}
                  </div>
                </div>
                {templateSubTab === 'email' && (<>
                <div className="space-y-3">
                  <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-blue-500" /><p className="text-[10px] font-black uppercase tracking-wider text-slate-600">Email Template</p></div>
                  <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">Subject Line</label><input type="text" value={msgTemplates.email_subject} onChange={e => { setMsgTemplates(prev => ({ ...prev, email_subject: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); }} placeholder="Invoice #{invoice} from {company}" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all" /></div>
                  <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">Email Body</label><textarea value={msgTemplates.email_body} onChange={e => { setMsgTemplates(prev => ({ ...prev, email_body: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} placeholder="Dear {name}, ..." rows={1} style={{ minHeight: '80px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">This becomes the HTML email body. Line breaks are preserved.</p></div>
                </div>
                </>)}
                {templateSubTab === 'whatsapp' && (<>
                <div className="space-y-3">
                  <div className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg><p className="text-[10px] font-black uppercase tracking-wider text-slate-600">WhatsApp Message</p></div>
                  <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">Caption sent with PDF</label><textarea value={msgTemplates.whatsapp_message} onChange={e => { setMsgTemplates(prev => ({ ...prev, whatsapp_message: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} placeholder="Hi {name}! Your invoice #{invoice} ..." rows={1} style={{ minHeight: '80px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-green-200 rounded-lg text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Sent as the caption when PDF is shared via WhatsApp.</p></div>
                </div>
                </>)}
                {templateSubTab === 'fitness' && (<>
                <div className="space-y-3">
                  <div className="flex items-center gap-2"><span className="text-base">💪</span><p className="text-[10px] font-black uppercase tracking-wider text-slate-600">Fitness & Food & Dining Templates</p></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">✅ Check-In Message</label><textarea value={msgTemplates.gym_checkin_message} onChange={e => { setMsgTemplates(prev => ({ ...prev, gym_checkin_message: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-purple-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{time}'} {'{date}'} {'{slot}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">🚪 Check-Out Message</label><textarea value={msgTemplates.gym_checkout_message} onChange={e => { setMsgTemplates(prev => ({ ...prev, gym_checkout_message: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-purple-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{time}'} {'{date}'} {'{slot}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">🍽️ Food & Dining Slot Message</label><textarea value={msgTemplates.canteen_slot_message} onChange={e => { setMsgTemplates(prev => ({ ...prev, canteen_slot_message: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-emerald-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{slot}'} {'{time}'} {'{date}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">📊 Weekly Report</label><textarea value={msgTemplates.weekly_report_whatsapp} onChange={e => { setMsgTemplates(prev => ({ ...prev, weekly_report_whatsapp: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-blue-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{icon}'} {'{module}'} {'{dayRows}'} {'{attended}'} {'{total}'} {'{pct}'} {'{missedLine}'} {'{encouragement}'}</p></div>
                    <div className="space-y-1.5 sm:col-span-2"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">📲 QR Badge Share</label><textarea value={msgTemplates.qr_badge_whatsapp} onChange={e => { setMsgTemplates(prev => ({ ...prev, qr_badge_whatsapp: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-amber-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{icon}'} {'{module}'} {'{plan}'} {'{start}'} {'{end}'} {'{slots}'}</p></div>
                  </div>
                </div>
                </>)}
                {templateSubTab === 'membership' && (<>
                <div className="space-y-3">
                  <div className="flex items-center gap-2"><span className="text-base">🔔</span><p className="text-[10px] font-black uppercase tracking-wider text-slate-600">Membership & Employee Attendance</p></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5 sm:col-span-2"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">💳 Membership Payment Link</label><textarea value={msgTemplates.membership_payment_link} onChange={e => { setMsgTemplates(prev => ({ ...prev, membership_payment_link: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-violet-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{currency}'} {'{amount}'} {'{link}'} {'{plan}'} {'{start}'} {'{end}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">⏰ Membership Expiring Soon</label><textarea value={msgTemplates.membership_expiring} onChange={e => { setMsgTemplates(prev => ({ ...prev, membership_expiring: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-orange-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{icon}'} {'{name}'} {'{module}'} {'{plan}'} {'{end}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">❌ Membership Expired</label><textarea value={msgTemplates.membership_expired} onChange={e => { setMsgTemplates(prev => ({ ...prev, membership_expired: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-red-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{icon}'} {'{name}'} {'{module}'} {'{plan}'} {'{end}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">✅ Employee Check-In</label><textarea value={msgTemplates.employee_checkin} onChange={e => { setMsgTemplates(prev => ({ ...prev, employee_checkin: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-teal-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{time}'} {'{date}'}</p></div>
                    <div className="space-y-1.5"><label className="text-[9px] font-black uppercase tracking-wider text-slate-400">👋 Employee Check-Out</label><textarea value={msgTemplates.employee_checkout} onChange={e => { setMsgTemplates(prev => ({ ...prev, employee_checkout: e.target.value })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} rows={1} style={{ minHeight: '72px', overflow: 'hidden' }} className="w-full px-3 py-2 bg-slate-50 border border-cyan-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:bg-white transition-all font-mono" /><p className="text-[9px] text-slate-400">Vars: {'{name}'} {'{time}'} {'{date}'}</p></div>
                  </div>
                </div>
                </>)}

                <div className="flex items-center justify-between pt-1">
                  <button onClick={() => { setMsgTemplates(brandTemplates({ ...DEFAULT_TEMPLATES })); setMsgTemplatesDirty(true); setMsgTemplatesSaved(false); }} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors">Reset to defaults</button>
                  <button onClick={async () => { setMsgSaving(true); try { await saveMessageTemplates(msgTemplates, selectedTemplateOutlet); setMsgTemplatesDirty(false); setMsgTemplatesSaved(true); setTimeout(() => setMsgTemplatesSaved(false), 2500); } catch { alert('Failed to save templates'); } finally { setMsgSaving(false); } }} disabled={!msgTemplatesDirty || msgSaving} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all ${msgTemplatesSaved ? 'bg-emerald-600 text-white' : msgTemplatesDirty ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm' : 'bg-slate-100 text-slate-400 cursor-default'}`}>
                    {msgTemplatesSaved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> {msgSaving ? 'Saving...' : 'Save Templates'}</>}
                  </button>
                </div>
              </div>
            )}
          </>)}

          {/* ── WHATSAPP TAB ── */}
          {activeTab === 'whatsapp' && perms.canManageProfileMessaging && (<>
  <div><h1 className="text-lg font-bold text-slate-900">WhatsApp</h1><p className="text-xs text-slate-500 mt-0.5">Connect WhatsApp to auto-send messages to customers</p></div>
  <WhatsAppConnectCard outletId={outletId} />
  <OutletsWhatsAppSection authUser={authUser} moduleStatuses={moduleStatuses} outletId={outletId} />
</>)}

          {/* ── EMAIL TAB ── */}
          {activeTab === 'email' && perms.canManageProfileMessaging && (<>
  <div><h1 className="text-lg font-bold text-slate-900">Email</h1><p className="text-xs text-slate-500 mt-0.5">Connect Gmail to send invoices from your own account</p></div>
  <OutletsGmailSection authUser={authUser} moduleStatuses={moduleStatuses} outletId={outletId} />
</>)}

          {/* ── GATEWAYS TAB ── */}
          {activeTab === 'gateways' && perms.canManageGateways && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Payment Gateways</h1><p className="text-xs text-slate-500 mt-0.5">Connect gateways so customers can pay bills online</p></div>
            <OutletsPaymentGatewaySection authUser={authUser!} outletId={outletId} />
          </>)}

          {((activeTab === 'currency' && !perms.canManageCurrency) ||
            (activeTab === 'whatsapp' && !perms.canManageProfileMessaging) ||
            (activeTab === 'email' && !perms.canManageProfileMessaging) ||
            (activeTab === 'gateways' && !perms.canManageGateways)) && (
            <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-6 text-center">
              <p className="text-sm font-bold text-slate-800">🔒 Access required</p>
              <p className="text-xs text-slate-500 mt-1">This section is restricted by your organization's permissions. Ask your owner to grant access if you need it.</p>
            </div>
          )}

          {/* ── COUNTERS TAB ── */}
          {activeTab === 'counters' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Serial Counters</h1><p className="text-xs text-slate-500 mt-0.5">Backend-assigned numbers per document type</p></div>
            {authUser && <POSCountersCard authUser={authUser} token={localStorage.getItem('cloudairambo_jwt') ?? ''} />}
          </>)}

          {/* ── DEVICES TAB ── */}
{activeTab === 'devices' && (<>
  <div><h1 className="text-lg font-bold text-slate-900">Devices</h1><p className="text-xs text-slate-500 mt-0.5">ZKTeco / USB fingerprint agent — per outlet</p></div>
  <OutletsAttendanceSection authUser={authUser} outletId={outletId} />
</>)}

          {/* ── BADGE TAB ── */}
          {activeTab === 'badge' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Cashier Badge</h1><p className="text-xs text-slate-500 mt-0.5">Scan at POS terminal to instantly log in</p></div>
            {authUser && <CashierBadgeCard authUser={authUser} />}
          </>)}
          {/* ── SUBSCRIPTION TAB ── */}
          {activeTab === 'subscription' && (<>
            <div><h1 className="text-lg font-bold text-slate-900">Subscription</h1><p className="text-xs text-slate-500 mt-0.5">Manage your plan and module subscriptions</p></div>
            {activeTab === 'subscription' && (
  <SubscriptionPlansView
    currentPlan={(authUser as any)?.plan ?? 'basic'}
    currentUserData={authUser}
    embedded={true}
  />
)}
          </>)}

          {/* ── DELETE ACCOUNT TAB ── */}
          {activeTab === 'delete' && authUser && (
            <DeleteAccountSection authUser={authUser} onLogout={onLogout} />
          )}

        </div>
      </div>
    </div>
  </>
  );
}

     
